import type { Translations } from '../index';

export const en: Translations = {
  errors: {
    generic: {
      unknownTitle: "Something didn't go through",
      unknownMessage: 'Your work is safe. Please try that action again in a moment.',
      badRequestTitle: "We couldn't process that request",
      badRequestMessage: 'Please review the highlighted details and try again.',
      notFoundTitle: 'Not found',
      notFoundMessage: "We couldn't find what you were looking for. It may have been moved or removed.",
      conflictTitle: 'This already exists',
      conflictMessage: 'This item already exists or was changed elsewhere. Refresh and try again.',
      unsupportedFileTitle: 'Unsupported file',
      unsupportedFileMessage: 'That file type isn’t supported here. Please try a different file.',
      fileTooLargeTitle: 'File too large',
      fileTooLargeMessage: 'That file exceeds the allowed size. Please try a smaller file.',
      retry: 'Try again',
      dismiss: 'Dismiss',
    },
    network: {
      offlineTitle: 'You appear to be offline',
      offlineMessage: 'Check your internet connection and try again.',
      timeoutTitle: 'This is taking longer than expected',
      timeoutMessage: 'The request timed out. Your work is saved — please try again.',
      serviceUnavailableTitle: 'Service temporarily unavailable',
      serviceUnavailableMessage: "We're briefly unavailable. Please try again in a few seconds.",
      serverErrorTitle: 'Our service is having trouble',
      serverErrorMessage: "We've been notified and are working on it. Please try again shortly.",
    },
    auth: {
      sessionExpiredTitle: 'Your session expired',
      sessionExpiredMessage: 'For your security, please sign in again to continue.',
      signInCta: 'Sign in',
      invalidCredentialsTitle: 'Incorrect email or password',
      invalidCredentialsMessage: 'Double-check your credentials and try again.',
      accountLockedTitle: 'Account temporarily locked',
      accountLockedMessage: 'Too many attempts. Please try again in a few minutes.',
      accountDeactivatedTitle: 'Account deactivated',
      accountDeactivatedMessage: 'This account is deactivated. Please contact your administrator.',
      forbiddenTitle: "You don't have access",
      forbiddenMessage: "You don't have permission for this action.",
      invalidCodeTitle: "That code didn't work",
      invalidCodeMessage: 'The verification code is invalid or expired. Request a new one.',
      currentPasswordWrongTitle: 'Current password is incorrect',
      currentPasswordWrongMessage: 'Re-enter your current password and try again.',
      userExistsTitle: 'Account already exists',
      userExistsMessage: 'An account with this email already exists. Try signing in.',
    },
    credits: {
      insufficientTitle: 'Not enough credits',
      insufficientMessage: "You don't have enough credits to complete this. Add credits or upgrade your plan to continue.",
      buyCreditsCta: 'Buy credits',
      minAmountTitle: 'Amount too low',
      minAmountMessage: 'The minimum purchase is 10 credits.',
      invalidAmountTitle: 'Enter a valid amount',
      invalidAmountMessage: 'Please enter a valid number of credits.',
      noPaymentMethodTitle: 'Add a payment method',
      noPaymentMethodMessage: 'Add a payment method before continuing.',
      checkoutFailedTitle: "Checkout couldn't start",
      checkoutFailedMessage: 'We couldn’t open checkout. Please try again.',
    },
    orders: {
      notFoundTitle: 'Order not found',
      notFoundMessage: "This order doesn't exist or you don't have access to it.",
      accessDeniedTitle: 'Access denied',
      accessDeniedMessage: "You don't have access to this order.",
      createFailedTitle: "We couldn't create your order",
      createFailedMessage: 'Something interrupted order creation. Your file is still here — please try again.',
    },
    corpus: {
      notFoundTitle: 'Corpus not found',
      notFoundMessage: 'This corpus doesn’t exist or was removed.',
      duplicateTermTitle: 'Duplicate term',
      duplicateTermMessage: 'This term is already in the glossary.',
    },
    plan: {
      limitTitle: "Your plan's limit reached",
      limitMessage: "You've reached what your current plan allows. Upgrade to continue.",
      upgradeCta: 'Upgrade plan',
      alreadyFreeTitle: "You're on the Free plan",
      alreadyFreeMessage: 'You are already on the Free plan. Choose a paid plan to unlock more.',
    },
    processing: {
      timeoutTitle: 'Processing is taking too long',
      timeoutMessage: 'The document service timed out. Your upload is safe — please try again.',
      failedTitle: "We couldn't finish processing",
      failedMessage: 'Something interrupted processing. Please try again.',
    },
  },
  formatTemplates: {
    title: 'Format templates',
    subtitle: "Define paper size, margins, font, header, page numbering, filler dashes and closing formula. The template is applied in Option 4 when reviewing an order.",
    adminTitle: 'Global format templates',
    adminSubtitle: "EZDocu templates available to every account. Each account chooses which one to turn on (or none); they are never applied automatically.",
    newTemplate: 'New template',
    newGlobalTemplate: 'New global template',
    duplicateBtn: 'Duplicate',
    duplicateFromGlobalHint: 'Creates your own editable copy of this EZDocu template',
    editBtn: 'Edit',
    deleteBtn: 'Delete',
    confirmDeleteBtn: 'Delete?',
    setDefaultBtn: 'Set as default',
    defaultBadge: 'Default',
    globalBadge: 'EZDocu',
    ownBadge: 'Own',
    myTemplates: 'My templates',
    globalTemplates: 'EZDocu templates',
    filterAll: 'All',
    filterGlobal: 'Global',
    filterAccounts: 'Accounts',
    loading: 'Loading templates…',
    empty: 'You have no templates of your own yet. Duplicate an EZDocu one or create a new one.',
    emptyAdmin: 'No templates.',
    copySuffix: '(copy)',
    errorGeneric: 'The template could not be saved',
    summaryHeader: 'Header',
    summaryNumbering: 'Numbering',
    summaryFill: 'Dashes',
    summaryClosing: 'Closing',
    yes: 'yes',
    off: 'off',
    sectionGeneral: 'General',
    sectionPage: 'Paper and margins',
    sectionTypography: 'Typography',
    sectionHeaderFooter: 'Header and footer',
    sectionNumbering: 'Page numbering',
    sectionFill: 'Short-line filler',
    sectionClosing: 'Closing formula',
    fieldName: 'Name',
    fieldDescription: 'Description',
    fieldPageFormat: 'Paper size',
    fieldOrientation: 'Orientation',
    portrait: 'Portrait',
    landscape: 'Landscape',
    a4: 'A4',
    letter: 'Letter',
    legal: 'Legal',
    fieldMarginTop: 'Top (mm)',
    fieldMarginBottom: 'Bottom (mm)',
    fieldMarginLeft: 'Left (mm)',
    fieldMarginRight: 'Right (mm)',
    fieldFontFamily: 'Font',
    fieldFontSize: 'Size (pt)',
    fieldLineHeight: 'Line spacing',
    fieldTextAlign: 'Alignment',
    alignJustify: 'Justified',
    alignLeft: 'Left',
    alignCenter: 'Center',
    alignRight: 'Right',
    fieldParagraphSpaceAfter: 'After paragraph (pt)',
    fieldHeaderEnabled: 'Header on pages',
    fieldHeaderText: 'Header text',
    fieldHeaderAlign: 'Alignment',
    fieldHeaderMode: 'On which pages',
    headerModeAll: 'All',
    headerModeFirst: 'First only',
    headerModeRest: 'All but the first',
    fieldHeaderFontSize: 'Header size (pt; empty = body size)',
    fieldFooterEnabled: 'Footer',
    fieldFooterText: 'Footer text',
    fieldPageNumbering: 'Number pages',
    fieldPageNumberFormat: 'Format',
    numberFormatN: '1, 2, 3',
    numberFormatNdeM: 'Page 1 of N',
    numberFormat00n: '001, 002',
    fieldPageNumberStart: 'Start at',
    fieldPageNumberPosition: 'Position',
    posFooterCenter: 'Footer, centered',
    posFooterRight: 'Footer, right',
    posHeaderRight: 'Header, right',
    fieldFillShortLines: 'Fill short lines with dashes',
    fieldFillChar: 'Character',
    fieldFillScope: 'Where',
    fillScopeParagraphEnd: 'Last line of each paragraph',
    fillScopeLine: 'Every short line',
    fillScopeBlankOnly: 'Blank lines only',
    fieldClosingEnabled: 'Add closing formula',
    fieldClosingHtml: 'Text (simple HTML)',
    fieldClosingKeepTogether: 'Keep it with the last paragraph (never alone on a new page)',
    variablesHint: 'Available variables (click to copy):',
    previewTitle: "Live preview",
    previewDocxBtn: "Sample Word",
    previewDocxError: "Could not generate the sample Word file",
    mirrorMakeEditable: "Edit as my own format",
    mirrorSheetNote: "The document comes out exactly as it is today",
    verifiedBadge: "Verified",
    alreadyInMenu: "Already in your menu",
    detectSaveDirect: "Save as is",
    swipeForMore: "Swipe to see more formats →",
    orderFormatLabel: "Output format",
    orderFormatHint: "Decides how Option 4 comes out. You can also change it in Review, without reprocessing.",
    previewRealLoading: "Generating real preview…",
    closingLangLabel: "Sworn formula language",
    closingLangEs: "As written in the template (not translated)",
    closingLangTarget: "Translated to the order’s target language",
    smallMarginWarn: "Margins under 10 mm may be cut off when printing.",
    colEmpty: "Empty",
    colText: "Text",
    colPageNum: "Page number",
    colTextPlaceholder: "Column text",
    colRemove: "Remove",
    colAdd: "Add column",
    colSwitchToColumns: "Build with columns (up to 4)",
    fieldFooterRule: "Printed line above the footer",
    sectionRules: "Document rules",
    fieldLineNumbering: "Number the lines (court format)",
    fieldLinesPerPage: "Lines per page",
    fieldLineNumberRule: "Vertical rule",
    lineNumberingHint: "Numbers recalculate automatically when editing the Word file: they live in the section, never written as text.",
    pinBtn: "Pin",
    pinnedBadge: "Pinned",
    pinHint: "Pinned formats appear when preparing an order",
    addToMenuBtn: "Add to my menu",
    deleteInUseConfirm: "This format was used in {n} order(s). Delivered orders will not change. Delete it anyway?",
    searchPlaceholder: "Search formats…",
    catAll: "All",
    catGeneral: "General",
    catCertified: "Certified",
    catCourt: "Court",
    pickerNoPinned: "You have no pinned formats. Pin the ones you use in Formats to see them here.",
    uploadWordBtn: "Upload your Word",
    detectTitle: "Here is what your Word contains",
    detectReading: "Reading the file…",
    detectIntro: "These properties were read from {file}. Anything not found is marked as such — nothing is silently filled in.",
    detectNotFound: "Not found",
    detectYes: "Yes",
    detectNo: "No",
    detectHint: "In the editor you can complete what is missing and see the result on paper, live.",
    detectOpenEditor: "Open in the editor",
    detectFailed: "The file could not be read. Is it a .docx up to 20 MB?",
    previewPageOf: "Page {n} of {m}",
    previewTabScreen: "Screen",
    previewTabWord: "Word",
    previewTabPdf: "PDF",
    previewAllMatch: "Screen, Word and PDF match",
    previewSample1: "BIRTH CERTIFICATE",
    previewSample2: "I certify that this is a faithful translation of the original document I have had before me. The person concerned was born in the city indicated, and their details appear in the corresponding register.",
    previewSample3: "The record matches the document submitted, and the original shows no amendments or erasures.",
    previewClosingSignature: "Translator's signature and seal",
    previewPageBreak: "page break",
    mirrorNotice: "Mirror of Original: this format reproduces the document exactly as it comes out today. Page, typography and rules do not apply.",
    saveBtn: 'Save',
    savingBtn: 'Saving…',
    cancelBtn: 'Cancel',
    backBtn: 'Back',
    unsavedConfirm: 'You have unsaved changes. Leave without saving?',
    savedOk: 'Saved',
    unsavedTitle: 'Unsaved changes',
    unsavedStay: 'Stay',
    unsavedLeave: 'Leave without saving',
    variablesLangHint: "Language, month and date variables are written automatically in the translation's target language.",
    closingSignatureToggle: "Add signature and seal (image)",
    closingSignatureUpload: "Upload signature/seal image",
    closingSignatureRemove: "Remove image",
    createBtn: 'Create',
    defaultCardLabel: 'Default format template',
    defaultCardHint: "Turn it on to also get Option 4 with this format in your orders",
    defaultNone: "No template (Option 4 is not generated)",
    defaultManageLink: 'Manage templates',
    defaultSaved: 'Saved',
    pickerLabel: 'Template',
    pickerApplying: 'Applying…',
    pickerError: 'The template could not be applied',
    pickerNone: "Original",
  },
  sidebar: {
    formatTemplates: 'Formatting',
    hubspot: 'HubSpot',
    payments: 'Payments',
    campaignCodes: 'Codes',
    ambassadorApplications: 'Ambassador applications',
    ambassadors: 'Ambassadors',
    applications: 'Applications',
    invite: 'Invite',
    ambassadorAccount: 'Ambassador account',
    adminAccount: 'Admin panel',
    ambassadorHome: 'Home',
    ambassadorReferrals: 'My referrals',
    ambassadorCommissions: 'Commissions',
    ambassadorNetwork: 'My network',
    overview: 'Overview',
    accounts: 'Accounts',
    users: 'Users',
    logs: 'Logs',
    waitlist: 'Waitlist',
    templates: 'Templates',
    analytics: 'Analytics',
    dashboard: 'Dashboard',
    orders: 'Orders',
    team: 'Team',
    credits: 'Credits',
    certificate: 'Certificate',
    settings: 'Settings',
    myTasks: 'My Tasks',
    myWorkspace: 'My Workspace',
    account: 'Account',
    activity: 'Activity',
    plans: 'Plans',
    features: 'Features',
    signOut: 'Sign out',
    closeSidebar: 'Close sidebar',
    openSidebar: 'Open sidebar',
    accountMenu: 'Account menu',
    corpus: 'Glossary',
    myTerms: 'My Terms',
    companySettings: 'Company',
    companyDataDesc: 'The account\'s company details',
    blog: 'Blog',
    landingPages: 'Landing Pages',
    navMenu: 'Nav menu',
    glossary: 'Glossaries',
    siteCopy: 'Site copy',
    seo: 'SEO',
    apiKeys: 'API Keys',
    support: 'Support',
    knowledge: 'Knowledge',
    eventRegistrations: 'Raffles / Events',
    content: 'Content',
    events: 'Events',
  },
  dashboard: {
    welcome: 'Welcome back',
    subtitle: "Here's what's happening with your orders today.",
    newOrder: 'New Order',
    lowCredits: 'Low credits: {n} remaining',
    creditsBalance: 'Balance',
    ordersInProgress: 'Orders in progress',
    completedOrders: 'Completed orders',
    teamMembers: 'Team members',
    available: 'Available',
    low: 'Low',
    active: 'Active',
    recentOrders: 'Recent Orders',
    viewAll: 'View all',
    noOrdersYet: 'No orders yet',
    createFirstOrder: 'Create your first order',
    quickActions: 'Quick Actions',
    uploadDocument: 'Upload Document',
    startNewTranslation: 'Start a new translation',
    buyCredits: 'Buy Credits',
    addToBalance: 'Add to your balance',
    inviteTeam: 'Invite Team',
    addTeamMembers: 'Add team members',
    companyOverview: 'Company Overview',
    thisMonth: 'this month',
    totalOrders: 'Total orders',
    inProgress: 'In progress',
    completed: 'Completed',
    recentActivity: 'Recent Activity',
    translation: 'Translation',
    translationEdit: 'Translation Edit',
    lastActivity: 'Last activity',
    noActivityYet: 'No activity yet',
    pages: 'page(s)',
    topLanguages: 'Top Languages',
    creditActivity: 'Credit Activity',
    document: 'Document',
    languages: 'Lang',
    date: 'Date',
    ordersCount: 'orders',
    archivedCount: '{n} archived',
    prev: 'Prev',
    next: 'Next',
    noTransactions: 'No transactions yet',
    status: 'Status',
    ocrGenerated: 'OCR',
    ocrEdit: 'OCR Edit',
    download: 'Download',
    txTranslationCompleted: 'Order {order}: Translation completed ({pages} pages)',
    txCreditsPurchased: 'Credits purchased',
    txCreditsRefunded: 'Credits refunded',
    txCreditsAdjusted: 'Credits adjusted',
    txAutoRecharge: 'Auto recharge',
  },
  statuses: {
    uploaded: 'Uploaded',
    processing: 'Processing',
    ocrreview: 'OCR Review',
    translationreview: 'Translation Review',
    completed: 'Completed',
    cancelled: 'Cancelled',
    unknown: 'Unknown',
  },
  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all read',
    noNotifications: 'No notifications yet',
    dismiss: 'Dismiss',
  },
  time: {
    justNow: 'Just now',
    minAgo: '{n} min ago',
    hourAgo: '{n} hour ago',
    hoursAgo: '{n} hours ago',
    yesterday: 'Yesterday',
    daysAgo: '{n} days ago',
    mAgo: '{n}m ago',
    hAgo: '{n}h ago',
    dAgo: '{n}d ago',
  },
  orders: {
    title: 'Orders',
    subtitle: 'Manage your translation orders',
    newOrder: 'New Order',
    total: 'Total',
    uploaded: 'Uploaded',
    inProgress: 'In Progress',
    completed: 'Completed',
    thisMonth: 'This Month',
    noOrdersFound: 'No orders found',
    tryAdjusting: 'Try adjusting your search or filters',
    createFirst: 'Create your first translation order to get started',
    order: 'Order',
    document: 'Document',
    type: 'Type',
    pagesCol: 'Pages',
    creditsCol: 'Credits',
    qualityCol: 'Quality',
    languageCol: 'Language',
    status: 'Status',
    createdBy: 'Created By',
    assignedTo: 'Assigned To',
    client: 'Client',
    date: 'Date',
    actions: 'Actions',
    showing: 'Showing',
    of: 'of',
    image: 'Image',
    text: 'Text',
    handwritten: 'Handwritten',
    modeTranslate: 'Translation',
    modeLayout: 'Layout',
    searchPlaceholder: 'Search by document name, creator, client, or order number...',
    allStatus: 'All Status',
    allTypes: 'All Types',
    allDocTypes: 'Document type',
    allWorkTypes: 'Work type',
    view: 'View',
    clearDates: 'Clear dates',
    verifiedBy: 'Verified by',
    due: 'Due',
    open: 'Open',
    filterProjects: 'Projects',
    filterSingle: 'Single orders',
    archived: 'Archived',
    toSetUp: 'To set up',
    cancelled: 'Cancelled',
    completedOf: '{pct}% completed · {done} of {total}',
    moveToProject: 'Move to project…',
    removeFromProject: 'Remove from project',
    notAssigned: 'Not assigned',
    trash: 'Trash',
    trashAction: 'Delete',
    trashConfirmTitle: 'Move to trash?',
    trashConfirmMessage: 'It will disappear from the lists, dashboard and projects for your whole team. You can restore it from the Trash for 7 days. No files or data are deleted.',
    trashConfirmButton: 'Move to trash',
    restore: 'Restore',
    deletePermanently: 'Delete permanently',
    deletePermanentlyConfirmTitle: 'Delete permanently?',
    deletePermanentlyConfirmMessage: "It will no longer be available to anyone on your team, not even in the Trash. This can't be undone.",
    emptyTrash: 'Empty trash',
    emptyTrashConfirmTitle: 'Empty the trash?',
    emptyTrashConfirmMessage: "All orders in the trash will be permanently deleted and will no longer be available to your team. This can't be undone.",
    trashNotice: 'Orders in the trash are permanently deleted after 7 days.',
    trashEmpty: 'The trash is empty',
    trashDaysLeft: '{days} d left',
    trashActionFailed: "The action couldn't be completed. Please try again.",
    trashNoPermission: "You don't have permission to delete orders.",
    working: 'Processing...',
    purgedBadge: 'Purged',
    archiveAction: 'Archive',
    unarchiveAction: 'Unarchive',
    archivedEmpty: 'No archived orders or projects',
    archiveFailed: "Couldn't archive the order. Please try again.",
    archivedBadge: 'Archived',
  },
  projects: {
    createOrder: 'Create order',
    createOrderHint: 'One document',
    createProject: 'Create project',
    createProjectHint: 'Several documents from the same client or case',
    newProject: 'New project',
    name: 'Project name',
    namePlaceholder: 'e.g. Maria Roldan Case',
    client: 'Client',
    clientPlaceholder: 'Client name',
    reference: 'Reference #',
    referencePlaceholder: 'Your own number',
    dueDate: 'Due date',
    addNote: '+ Add note',
    notePlaceholder: 'Notes for this case',
    defaults: 'Defaults for every document',
    task: 'What do you need?',
    translation: 'Translation',
    layout: 'Layout',
    decidePerDocument: 'Decide per document',
    assignProject: 'Assign entire project to',
    dropFiles: 'Drop files here or choose files',
    chooseFiles: 'Choose files',
    createCta: 'Create project',
    creating: 'Creating…',
    pastDate: 'This date is in the past.',
    afterProjectDue: 'This is after the project due date ({date}).',
    prepTitle: 'Preparation',
    addDocuments: 'Add documents',
    addNDocuments: 'Add {n} documents',
    languages: 'Languages',
    assignTo: 'Assign to',
    apply: 'Apply',
    statusReady: 'Ready',
    statusToSetUp: 'To set up',
    createNOrders: 'Create {n} orders',
    estimated: 'Estimated: {n} credits',
    remainingPrep: '{n} documents will remain in preparation',
    archive: 'Archive project',
    archiveConfirm: 'Archive this project? You can unarchive it later.',
    unarchive: 'Unarchive',
    downloadZip: 'Download all',
    setTaskToContinue: 'Set the task for each document to continue',
    creditsForXN: 'You have credits for {x} of {n} orders.',
    createX: 'Create {x}',
    recharge: 'Add credits',
    position: '#',
    auto: 'Auto',
    removePrep: 'Remove this document from preparation?',
  },
  teamPage: {
    title: 'Team',
    subtitle: 'Manage your team members and invitations',
    inviteMember: 'Invite Member',
    teamMembers: 'Team Members',
    pendingInvites: 'Pending Invites',
    noMembers: 'No team members yet. Invite someone to get started.',
    owner: 'Owner',
    active: 'Active',
    inactive: 'Inactive',
    remove: 'Remove',
    joined: 'Joined',
    pendingInvitations: 'Pending Invitations',
    expires: 'Expires',
    sent: 'Sent',
    link: 'Link',
    copied: 'Copied',
    resend: 'Resend',
    cancel: 'Cancel',
    teamRoles: 'Team Roles',
    teamRolesDesc:
      '<strong>Team</strong> members can create and manage orders. <strong>Members</strong> can work on assigned orders. Only account owners can invite new members and manage billing.',
    inviteTeamMember: 'Invite Team Member',
    invitationSent: 'Invitation Sent',
    firstName: 'First Name',
    lastName: 'Last Name',
    emailAddress: 'Email Address',
    sending: 'Sending...',
    sendInvitation: 'Send Invitation',
    invitationSentTo: 'Invitation sent to',
    inviteLink: 'Invite Link',
    shareLinkDesc: 'Share this link with the person to join your team.',
    done: 'Done',
    copy: 'Copy',
    member: 'Member',
    role: 'Role',
    status: 'Status',
    email: 'Email',
    removeTeamMember: 'Remove Team Member',
    removeConfirmMsg:
      'Are you sure you want to remove <strong>{name}</strong> from your team? This action cannot be undone. The member will lose access to all team resources immediately.',
    removeMember: 'Remove Member',
    thisMonth: 'This Month',
    roleOwner: 'Owner',
    roleManager: 'Manager',
    roleReviewer: 'Reviewer',
    roleMember: 'Member',
    roleManagerDesc: 'Runs the account day to day',
    roleReviewerDesc: "Views and approves the team's work",
    roleMemberDesc: 'Only their own and assigned documents',
    whatWillTheyDo: 'What will they do in the account?',
    permissionsHint: 'Permissions over the WHOLE account, not just their own work: whatever this person edits, the whole team sees. Only what you change is saved.',
    whatThisAllows: 'What this permission covers',
    scopeDocsViewAll: 'Sees and assigns ALL company orders, not just their own.',
    scopeDocsApprove: 'Approves and finalizes account documents, including ones they did not work on.',
    scopeDocsDelete: 'Opens any order and deletes its data and files; they are not recoverable.',
    scopeCorpusEdit: 'Corpus and glossaries belong to the company: changes affect the whole team\'s translations.',
    scopeCertManage: 'Certificate templates belong to the company: every member uses them.',
    scopeBilling: 'Sees the balance and BUYS credits with the company payment method.',
    scopeTeam: 'Invites, activates/deactivates and edits other account members.',
    scopeAccountEdit: 'Changes the company details (name, legal name, tax ID, logo) for everyone.',
    scopeLogs: 'Sees the activity of ALL account users, not just their own.',
    adjustedBadge: 'Adjusted',
    permDocsViewAll: 'View and assign all documents',
    permDocsApprove: 'Approve and finalize documents',
    permDocsDelete: 'Delete documents',
    permCorpusEdit: 'Edit corpus and glossaries',
    permCertManage: 'Edit certificate templates',
    permBilling: 'View and buy credits',
    permTeam: 'Invite and deactivate members',
    permAccountEdit: 'Edit company details',
    permLogs: 'View activity log',
    readOnlyBadge: 'Read only',
    adjustedOne: 'adjusted',
    adjustedMany: 'adjusted',
    permCountOne: 'permission',
    permCountMany: 'permissions',
    edit: 'Edit',
    editMemberTitle: 'Edit Member',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    readOnlyLabel: 'Read only',
    readOnlyHint: "Can view the team's work but not change it.",
  },
  creditsPage: {
    title: 'Credits',
    subtitle: 'Manage your credits and billing',
    availableBalance: 'Available Balance',
    creditsInfo: 'credits',
    usedThisMonth: 'Used This Month',
    totalPurchased: 'Total Purchased',
    yourSubscription: 'Your Subscription',
    cancelSubscription: 'Cancel Subscription',
    currentPlan: 'Current Plan',
    unknown: 'Unknown',
    subscriptionEnds: 'Subscription Ends',
    nextBillingDate: 'Next Billing Date',
    willNotRenew: 'Will not renew',
    paymentMethod: 'Payment Method',
    change: 'Change',
    card: 'Card',
    expiresDate: 'Expires',
    autoRenew: 'Auto-Renew',
    on: 'On',
    off: 'Off',
    updating: 'Updating...',
    subscriptionEndingSoon: 'Subscription ending soon',
    subscriptionEndingDesc:
      'Your subscription will end on {date}. Reactivate to keep your benefits.',
    reactivateNow: 'Reactivate Now',
    cancelSubscriptionQ: 'Cancel Subscription?',
    cancelSubscriptionDesc:
      'Are you sure you want to cancel your <strong>{plan}</strong> subscription? You\'ll continue to have access until <strong>{date}</strong>.',
    keepSubscription: 'Keep Subscription',
    yesCancel: 'Yes, Cancel',
    changePlanQ: 'Change Plan?',
    newPlan: 'New Plan',
    changePlanDesc:
      'Your current <strong>{current}</strong> plan stays until you pay. After payment that subscription is cancelled and <strong>{new}</strong> starts immediately.',
    changePlan: 'Change Plan',
    alreadySubscribed: 'Already Subscribed',
    alreadySubscribedDesc:
      'You are already subscribed to the <strong>{plan}</strong> plan.',
    gotIt: 'Got it',
    autoRechargeCredits: 'Auto-Recharge Credits',
    whenBalanceBelow: 'When Balance Below',
    credits: 'credits',
    rechargeAmount: 'Recharge Amount',
    autoRecharge: 'Auto-Recharge',
    willAdd:
      'Will add <strong>{amount} credits</strong> when balance falls below <strong>{threshold}</strong>',
    autoRechargeDisabled: 'Auto-recharge is disabled',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    noPaymentMethod: 'No payment method',
    addPaymentMethodFirst:
      'You need to add a payment method first. Make a purchase or subscribe to a plan to enable auto-recharge.',
    addPaymentMethodForRenewal: 'Add payment method to renew',
    addPaymentMethodTitle: 'Add a payment method for renewal',
    addPaymentMethodForRenewalDesc:
      'Your plan is active for this period. Add a card so the next billing cycle can be charged — you will not be charged today.',
    pendingPaymentMethodStatus: 'Active — payment method needed',
    nextBillingAfterPaymentMethod: 'After you add a card',
    buyCreditPackages: 'Buy Credit Packages',
    noPackagesAvailable: 'No credit packages available',
    mostPopular: 'Most Popular',
    buyNow: 'Buy Now',
    buyCustomAmount: 'Buy Custom Amount',
    buyCustomDesc:
      'Need a specific amount? Enter how many credits you want (1 credit = $1 USD)',
    enterCreditsMin: 'Enter credits (min. 10)',
    subscriptionPlans: 'Subscription Plans',
    monthly: 'Monthly',
    yearly: 'Yearly',
    saveUpTo: 'Save up to 20%',
    noPlansAvailable: 'No subscription plans available',
    year: 'year',
    month: 'month',
    orMonthly: 'or ${price}/month billed monthly',
    orYearly: 'or ${price}/year (save {discount}%)',
    creditsIncluded: '{n} credits',
    noCreditsIncluded: 'No credits included',
    imageBasedPages: 'Image-based Pages',
    creditPerPage: '1 credit / page',
    imageBasedDesc: 'Scanned documents, images, PDFs requiring OCR',
    textBasedPages: 'Text-based Pages',
    textBasedDesc: 'Digital PDFs and documents with text layers',
    transactionHistory: 'Transaction History',
    noTransactions: 'No transactions yet',
    noTransactionsFilter: 'No transactions in this period',
    showLess: 'Show less',
    txFilterAll: 'All',
    txFilterToday: 'Today',
    txFilterWeek: 'This Week',
    txFilterMonth: 'This Month',
    exportCSV: 'Export CSV',
    txCount: 'transactions',
    txNet: 'Net',
    purchased: 'Purchased {n} credits',
    used: 'Used {n} credits',
    subscriptionHelp: 'Subscription plans provide a fixed amount of credits monthly or yearly to process your documents. These credits are added to your balance at the beginning of each billing period.',
    subscriptionHelpTitle: 'About Subscriptions',
    subscriptionHelpContent: 'Subscription plans provide a fixed amount of credits that are added to your balance at the start of each billing period (monthly or yearly). These credits can be used to process documents through OCR, translation, and other services.\n\nYour subscription renews automatically on each billing date. You can cancel at any time and keep access until the end of your current period. If you need more credits beyond what your plan includes, you can purchase additional credit packs or enable auto-recharge.',
    autoRechargeHelp: 'Auto-recharge automatically adds credits to your balance when it falls below a specific threshold, ensuring you never run out of credits during document processing. You can customize the threshold and the recharge amount.',
    autoRechargeHelpTitle: 'About Auto-Recharge',
    autoRechargeHelpContent: 'Auto-recharge monitors your credit balance and automatically purchases additional credits when it drops below the threshold you set. This ensures uninterrupted document processing without manual intervention.\n\nYou choose two values: the minimum balance threshold (when to recharge) and the recharge amount (how many credits to add). Your saved payment method will be charged automatically each time a recharge is triggered.',
    viewPlans: 'View Subscription Plans',
    close: 'Close',
    redeemCode: 'Promo code',
    redeemPlaceholder: 'Enter your code',
    redeemApply: 'Apply',
    redeemApplying: 'Applying…',
    redeemSuccess: 'Code applied',
    redeemError: 'Could not apply the code',
    redeemHint: 'Redeem a campaign code. Purchase benefits apply automatically at checkout.',
    redeemPreviewTrial: '{c} bonus credits',
    redeemPreviewDiscount: '{p}% off the plan price on Stripe',
    redeemPreviewDiscountMonths: ' for {n} months',
    redeemPreviewPurchase: '{x}x credits included with the plan',
    redeemApplied: 'You already redeemed {code}',
    redeemAppliedDiscount: 'The {p}% discount is already on the plan prices.',
    redeemAppliedMultiplier: 'You will get {x}x included credits when you activate a plan.',
    redeemAppliedPacks: 'credit packs',
    redeemAppliedPlans: '{plans} plans',
    redeemAppliedNone: 'This code is already on your account.',
    planCardCodeDiscount: 'Price with your code {code}: {p}% off',
    planCardCodeMultiplier: 'Your code {code} multiplies plan credits by {x}x',
  },
  paywall: {
    teamTitle: 'Team is included in the Team plan',
    teamMessage: 'You need to activate a Team plan to invite members and manage your team.',
    corpusTitle: 'Glossaries are included in the Team plan',
    corpusMessage: 'You need to activate a Team plan to create term sets and use them in translations.',
    cta: 'Activate a Team plan',
    openingCheckout: 'Opening Stripe…',
  },
  planGate: {
    teamTitle: 'Activate a Team plan to use Team',
    teamBody: 'The Free plan does not include team management. Choose Team to invite members and collaborate.',
    activityTitle: 'Activate a Team plan to see team activity',
    activityBody: 'The Free plan does not include team activity history. Activate Team to unlock it.',
    activateTeamCta: 'Activate Team plan',
    openingCheckout: 'Opening Stripe…',
    memberTitle: 'Your team needs a plan',
    memberBody: 'This account is on the Free plan. Ask your team admin to purchase a plan so you can keep working.',
  },
  settingsPage: {
    unsavedTitle: "You have unsaved changes",
    unsavedMessage: "If you leave now, the changes on this screen will be lost.",
    unsavedSaveAndLeave: "Save and leave",
    unsavedLeave: "Leave without saving",
    unsavedStay: "Stay here",
    personalInfo: 'Personal Information',
    personalInfoDesc: 'Basic account information',
    whatsappAvailable: 'Available on WhatsApp',
    dateFormat: 'Date Format',
    dateFormatHint: 'For the interface. Certifications use the target country format.',
    markerStyleLabel: 'Visual element notation',
    seccionEmpresa: 'Company',
    correoEmpresa: 'Company email',
    telefonoEmpresa: 'Company phone',
    seccionEmpresaDesc: 'Company data, shared by the whole team',
    seccionPerfil: 'Profile',
    seccionPerfilDesc: 'Your personal data and preferences',
    managedByAgency: 'Managed by your agency',
    usarLaDeLaEmpresa: 'Use company setting',
    perfilNoCargado: 'Your profile could not be loaded. Reload the page before saving.',
    markerStyleHint: 'Delimiter style for logos, seals and signatures in your translations.',
    markerBrackets: 'Brackets',
    markerBraces: 'Braces',
    interfaceLanguage: 'Interface Language',
    legalName: 'Legal Name',
    legalNamePlaceholder: 'Legal name for invoices',
    taxId: 'Tax ID',
    taxIdHint: 'For corporate invoices',
    website: 'Website',
    jobTitle: 'Your job title',
    jobTitlePlaceholder: 'CEO, Project Manager...',
    linkedinCompanyPage: 'Company LinkedIn page',
    preferredContactMethod: 'Preferred contact method',
    preferredContactNone: 'No preference',
    preferredContactCall: 'Phone call',
    emailVerifyBannerTitle: 'Verify your email address',
    emailVerifyBannerDesc: 'Confirm this is your email:',
    emailVerifySendCode: 'Send code',
    emailVerifySending: 'Sending…',
    emailVerifyCta: 'Verify',
    emailVerifyVerifying: 'Verifying…',
    emailVerifyError: 'Could not send the code. Please try again.',
    title: 'Settings',
    subtitle: 'Manage your account and preferences',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    saved: 'Saved!',
    profile: 'Profile',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    accountSection: 'Account',
    companyName: 'Company Name',
    changeLogo: 'Change Logo',
    uploadLogo: 'Upload Logo',
    uploading: 'Uploading...',
    remove: 'Remove',
    notificationsSection: 'Notifications',
    orderStatusUpdates: 'Order status updates',
    orderStatusUpdatesDesc: 'Get notified when order status changes',
    teamActivity: 'Team activity',
    teamActivityDesc: 'Notifications about team member actions',
    lowCreditAlerts: 'Low credit alerts',
    lowCreditAlertsDesc: 'Alert when credits fall below 50',
    marketingEmails: 'Marketing emails',
    marketingEmailsDesc: 'Tips, product updates, and offers',
    timezone: 'Timezone',
    timezoneDesc: 'Set your timezone to display dates and times correctly.',
    current: 'Current',
    security: 'Security',
    changePassword: 'Change Password',
    sessions: 'Sessions',
    activeSession: '1 active session',
    signOutAllDevices: 'Sign out all devices',
    billing: 'Billing',
    currentPlan: 'Current Plan',
    payAsYouGo: 'Pay-as-you-go',
    noPlanActive: 'No active plan',
    noPlanDesc: 'Contact your company administrator for plan access',
    noPlanDescFreelancer: 'Purchase credits to start translating',
    viewInvoices: 'View Invoices',
    changePasswordModal: 'Change Password',
    changePasswordDesc: 'Enter your current password and choose a new one.',
    currentPassword: 'Current Password',
    enterCurrentPassword: 'Enter current password',
    newPassword: 'New Password',
    minChars: 'Min. 8 characters',
    confirmNewPassword: 'Confirm New Password',
    confirmYourPassword: 'Confirm your password',
    changingPassword: 'Changing password...',
    passwordChanged: 'Password Changed!',
    passwordChangedDesc: 'Your password has been changed successfully.',
    done: 'Done',
    passwordsNoMatch: 'Passwords do not match',
    passwordMinLength: 'Password must be at least 8 characters',
    logoMaxSize: 'Logo must be less than 5MB',
    logoFormatHint: 'JPG, PNG or WEBP. Max 5MB.',
    helpTitle: 'Need Help?',
    helpDesc: 'Our team is here to help you with any questions.',
    helpEmail: 'Contact Support',
    helpDocs: 'Documentation',
    privacyPolicy: 'Privacy Policy',
    termsConditions: 'Terms & Conditions',
    allRightsReserved: 'All rights reserved.',
    // Privacy & Data Retention
    privacyRetention: 'Privacy & Data Retention',
    privacyRetentionDesc: 'Configure automatic deletion of completed order data after a retention period.',
    retentionPolicy: 'Retention Policy',
    retentionPolicyDesc: 'Completed orders will be automatically purged after the selected period. A KMS-signed deletion certificate is generated for each purge.',
    retentionDisabled: 'Disabled',
    retentionDays: '{n} days',
    retentionSaving: 'Saving...',
    retentionSaved: 'Saved!',
    retentionNone: 'No auto-deletion',
    retentionDay: '{n} day',
    retentionDaysLabel: '{n} days',
    retentionWarning: 'Changing retention will reschedule all pending purge timers for completed orders.',
    retentionImmediate: 'Immediate',
    retentionImmediateWarning: 'When an order is completed, all its data and files will be permanently deleted immediately. A deletion certificate will be generated as proof.',
    retentionActive: 'Active',
    retentionInactive: 'Inactive',
    deletionCertificates: 'Deletion Certificates',
    deletionCertificatesDesc: 'View cryptographically signed certificates for purged orders.',
    viewCertificates: 'View Certificates',
    noCertificates: 'No deletion certificates yet.',
    downloadCertificate: 'Download PDF',
    previewCertificate: 'Preview',
    // Timezone picker
    searchCity: 'Search city...',
    noResults: 'No results',
    // Deletion certificate detail modal
    certDetail: {
      certificateOne: 'certificate',
      certificateMany: 'certificates',
      backToList: 'Back to list',
      order: 'Order',
      document: 'Document',
      account: 'Account',
      deletedAt: 'Deleted At',
      method: 'Method',
      retention: 'Retention',
      retentionDaysTpl: '{n} days',
      integrity: 'Integrity',
      valid: 'Valid',
      invalid: 'Invalid',
      deletedFilesTpl: 'Deleted Files ({n})',
      purgedFieldsTpl: 'Purged Fields ({n})',
      prev: 'Prev',
      next: 'Next',
    },
    professionalProfile: 'Professional Profile',
    professionalProfileDesc: 'Information used on certificates and official documents',
    phone: 'Phone',
    phonePlaceholder: '+1234567890',
    phoneHint: 'International format (E.164)',
    address: 'Address',
    addressPlaceholder: 'City, State, Country',
    association: 'Professional Association',
    associationNoneOption: 'None',
    associationOther: 'Other',
    associationCustomLabel: 'Association Name',
    certificationNumber: 'Certification / Member Number',
    languagePairs: 'Language Pairs',
    languagePairsDesc: 'Source and target languages you translate',
    addLanguagePair: 'Add Pair',
    sourceLanguage: 'Source',
    targetLanguage: 'Target',
    noLanguagePairs: 'Add at least one language pair',
    removePair: 'Remove',
    signatureImage: 'Signature Image',
    signatureHint: 'JPG or PNG. Max 2MB.',
    signatureInvalidFormat: 'Signature must be JPG or PNG',
    signatureMaxSize: 'Signature must be less than 2MB',
    uploadSignature: 'Upload Signature',
    changeSignature: 'Change',
    removeSignature: 'Remove',
    saveProfile: 'Save Profile',
  },
  certificatePage: {
    title: 'Certificate',
    subtitle: 'This certificate is automatically added as the last page of every translated PDF',
    uploadDocx: 'Upload .docx',
    newTemplate: 'New Template',
    editingTemplate: 'Editing certificate template',
    variables: 'Variables',
    saveAndClose: 'Save & Close',
    saving: 'Saving...',
    insertVariable: 'Insert variable:',
    dynamicVariables: 'Dynamic Variables',
    variablesDesc: 'Click to copy \u00b7 These get replaced automatically in the final certificate',
    clickToCopy: 'Click any variable to copy it to clipboard',
    hideExamples: 'Hide examples',
    showExamples: 'Show examples',
    defaultTemplates: 'Default Templates',
    myTemplates: 'My Templates',
    otherTemplates: 'Other templates',
    setActive: 'Set Active',
    active: 'Active',
    global: 'Global',
    noTemplate: 'No certificate template',
    noTemplateDesc: 'Create one to automatically add it to your translated PDFs',
    createTemplate: 'Create Template',
    uploadTemplate: 'Upload Template',
    name: 'Name',
    namePlaceholder: 'e.g. Certification of Accuracy',
    descriptionOptional: 'Description (optional)',
    descriptionPlaceholder: 'Brief description of this template...',
    category: 'Category',
    general: 'General',
    legal: 'Legal',
    immigration: 'Immigration',
    academic: 'Academic',
    cancel: 'Cancel',
    creating: 'Creating...',
    createAndEdit: 'Create & Edit',
    opening: 'Opening...',
    editTemplate: 'Edit Template',
    activateHint: 'Activate a template to add it to your collection',
    templateLibrary: 'Template Library',
    willBeAppended: 'This certificate will be appended to all completed orders',
    language: 'Language',
    anyLanguage: 'Any language',
    english: 'English',
    spanish: 'Spanish',
    portuguese: 'Portuguese',
  },
  orderDetail: {
    backToOrders: 'Back to Orders',
    continueOrder: 'Continue Order',
    error: 'Error',
    orderNotFound: 'Order not found',
    failedToLoad: 'Failed to load order',
    orderDetails: 'Order Details',
    documentType: 'Document Type',
    category: 'Category',
    sourceCountry: 'Source Country',
    targetCountry: 'Target Country',
    sourceLanguage: 'Source Language',
    targetLanguage: 'Target Language',
    totalPages: 'Total Pages',
    formatTemplate: 'Format template',
    noFormatTemplate: 'Original',
    credits: 'Credits',
    consumed: 'consumed',
    estimated: 'estimated',
    created: 'Created',
    createdBy: 'Created By',
    assignedTo: 'Assigned To',
    completed: 'Completed',
    exportFormat: 'Export Format',
    originalFile: 'Original File',
    view: 'View',
    openInNewTab: 'Open in new tab',
    download: 'Download',
    close: 'Close',
    accessDenied: 'Access Denied',
    adminNoAccess: 'Platform administrators cannot view document contents for security and privacy reasons. Only account owners, members, and freelancers can access documents.',
    translatedDocument: 'Translated Document',
    finalDocument: 'Laid-out Document',
    deletionCertificate: 'Deletion Certificate',
    translationCertificate: 'Translation Certificate',
    fileDeleted: 'This file has been deleted',
    sizeUnknown: 'Size unknown',
    uploadAndDetails: 'Upload & Details',
    ocr: 'OCR',
    review: 'Review',
    final: 'Final',
    deleteData: 'Delete Data',
    deleteOrderData: 'Delete Order Data',
    deleteOrderDesc: 'This will permanently delete all files (uploaded and completed) for:',
    deleteOrderPreserved: 'The order record and deletion certificate will be preserved. This action cannot be undone.',
    cancel: 'Cancel',
    dataDeletedSuccess: 'Data deleted successfully',
    dataDeletedFailed: 'Failed to delete data',
    certificateLabel: 'Certificate',
    // Human verification
    humanVerified: 'Human Verified',
    verify: 'Verify Translation',
    verifyConfirmTitle: 'Verify Translation',
    verifyConfirmDesc: 'You are certifying that this translation has been reviewed and approved by a human translator. This action will add a verification seal to the document.',
    verifyNotes: 'Notes (optional)',
    verifySuccess: 'Translation verified successfully',
    verifyFailed: 'Failed to verify translation',
    verifiedBy: 'Verified by',
    verifiedOn: 'Verified on',
    viewVerification: 'View Public Verification',
    // Data purged / deletion box
    dataPurged: 'Data Purged',
    loadingCertificate: 'Loading certificate...',
    allDataDeleted: 'All files and sensitive data have been permanently deleted.',
    filesDeletedTpl: '{n} files deleted',
    autoPurgeNoticeTpl: 'Auto-purge enabled — Files will be automatically deleted {time} after completion',
    retentionMin: 'min',
    retentionHour: 'hour',
    retentionHours: 'hours',
    retentionDay: 'day',
    retentionDays: 'days',
    inTrashBanner: 'This order is in the trash. It will be permanently deleted on {date}.',
    restoreOrder: 'Restore order',
    // Localized document type & category labels
    documentTypes: { image: 'Image', text: 'Text', handwritten: 'Handwritten' },
    categories: {
      birth_certificate: 'Birth Certificate',
      certificate: 'Certificate',
      diploma: 'Diploma',
      passport: 'Passport',
      marriage_license: 'Marriage License',
      medical: 'Medical Record',
      legal: 'Legal Document',
      immigration: 'Immigration Form',
      other: 'Other',
    },
  },
  workspace: {
    title: 'My Workspace',
    welcomeBack: 'Welcome back, {name}. You have {n} tasks assigned.',
    assigned: 'Assigned',
    tasksToComplete: 'Tasks to complete',
    active: 'Active',
    inProgress: 'In progress',
    completed: 'Completed',
    thisMonth: 'This month',
    pendingTasks: 'Pending Tasks',
    pendingTasksDesc: 'Orders pending your review and processing',
    noOrdersAssigned: 'No orders assigned to you yet',
    ordersWillAppear: 'Orders will appear here once they are assigned to you.',
    page: 'page',
    pages: 'pages',
    workflowTip: 'Workflow Tip',
    workflowTipDesc: 'Each order follows a workflow: <strong>Upload → OCR Review → Translation → Complete</strong>. You must verify and approve each step before proceeding to the next one.',
    startOrder: 'Start Order',
    continueAction: 'Continue',
    reviewAction: 'Review',
    viewAction: 'View',
    myOrders: 'My Orders',
    myOrdersSubtitle: 'All orders assigned to you',
    searchPlaceholder: 'Search orders...',
    totalAssigned: 'Total Assigned',
    noOrdersFound: 'No orders found',
    ordersAssignedAppear: 'Orders assigned to you will appear here.',
    document: 'Document',
    pagesCol: 'Pages',
    languages: 'Languages',
    status: 'Status',
    createdCol: 'Created',
    action: 'Action',
    pageOf: 'Page {p} of {total}',
    orders: 'orders',
    creditPackages: 'Credit Packages',
    purchaseCreditsDesc: 'Purchase credits to process your documents',
    viewAll: 'View All',
    uploadPhoto: 'Upload Photo',
    managePreferences: 'Manage your account preferences',
    notifications: 'Notifications',
    newTaskAssignments: 'New task assignments',
    newTaskAssignmentsDesc: 'Get notified when a new task is assigned to you',
    taskDeadlines: 'Task deadlines',
    taskDeadlinesDesc: 'Reminders for upcoming deadlines',
    commentsAndFeedback: 'Comments and feedback',
    commentsAndFeedbackDesc: 'Notifications when someone comments on your work',
    dailySummary: 'Daily summary',
    dailySummaryDesc: 'Receive a daily summary of your tasks',
    account: 'Account',
    organization: 'Organization',
    role: 'Role',
    yourStats: 'Your Stats',
    tasksCompleted: 'Tasks Completed',
    pagesTranslated: 'Pages Translated',
    avgQualityScore: 'Avg. Quality Score',
  },
  newOrder: {
    loadingOrder: 'Loading order...',
    restoreProgress: 'Please wait while we restore your progress',
    stepProgress: 'Step {step} of {total}',
    stepUploadDetails: 'Upload & Details',
    stepProcessing: 'Processing',
    stepReview: 'Review',
    stepTranslation: 'Translation',
    stepDownload: 'Download',
    modeLabel: 'Mode',
    modeTranslate: 'Translate',
    modeLayout: 'Layout',
    modeTranslateHint: 'Translate the document to another language, with certification.',
    modeLayoutHint: 'Layout only: reconstruct the document in its original language, no translation.',
    layoutReadyTitle: 'Document ready',
    layoutReadyDesc: 'Your laid-out document is ready to download in its original language.',
    downloadDocument: 'Download document',
    preparingDownload: 'Preparing…',
    error: 'Error',
    uploadDocument: 'Upload Document',
    acceptedFileTypes: 'PDF, Word (DOC, DOCX), JPG, PNG, TIFF, WebP',
    analyzingDocument: 'Analyzing document...',
    classifyingDocument: 'Classifying document...',
    loadingPreview: 'Loading preview...',
    fileTypePdf: 'PDF',
    fileTypeWord: 'Word',
    docxTooLargeToCount: 'Too large to auto-count pages — please check',
    fileTypeImage: 'Image',
    page: 'page',
    pages: 'pages',
    hasText: 'Has text',
    scanned: 'Scanned',
    fileAnalysis: 'File Analysis',
    complete: 'Complete',
    textLabel: 'Text:',
    selectable: 'Selectable',
    methodLabel: 'Method:',
    ocrMethod: 'OCR (1 cr/pg)',
    directMethod: 'Direct (1 cr/pg)',
    paperLetter: "Letter",
    paperA4: "A4",
    paperLegal: "Legal",
    paperPortrait: "portrait",
    paperLandscape: "landscape",
    handwritten: 'Handwritten',
    mixedContent: 'Mixed (printed + handwritten)',
    disableHandwritten: 'Click to process as scanned image',
    enableHandwritten: 'Click to restore handwritten mode',
    manuscriptSuggestionTitle: 'This looks like a scanned image',
    manuscriptSuggestionBody: 'For the cleanest result, use Manuscript mode. It turns your document into clear, editable text with a layout that matches the original. If you skip it, the text is placed over the scanned image, which looks rougher and is harder to edit.',
    manuscriptSuggestionConfirm: 'Use Manuscript mode',
    manuscriptSuggestionDismiss: 'Continue without it',
    manuscriptSuggestionBadge: 'Recommended',
    pageFormat: 'Paper format',
    pageFormatLetter: 'Letter',
    pageFormatLegal: 'Legal',
    orientationLabel: 'Orientation',
    orientationPortrait: 'Portrait',
    orientationLandscape: 'Landscape',
    orientationSuggested: 'suggested',
    readAloud: 'Read',
    stopReading: 'Stop',
    loadingAudio: 'Loading...',
    viewTranscript: 'View transcript',
    transcriptTitle: 'Transcribed Text',
    copyText: 'Copy',
    transcribingHandwriting: 'Transcribing handwritten text...',
    readingHandwriting: 'Reading handwritten text with AI...',
    processingPages: 'Processing pages...',
    generatingDocument: 'Generating document...',
    clientName: 'Client Name',
    clientNamePlaceholder: 'e.g., John Smith',
    reference: 'Reference #',
    dueDate: 'Due date',
    addInstructions: '+ Add instructions',
    detailsSection: 'Details',
    languagesSection: 'Languages & localization',
    teamSection: 'Team',
    documentName: 'Document Name',
    documentNamePlaceholder: 'e.g., Birth Certificate - Garcia',
    documentCategory: 'Document Category',
    sourceCountry: 'Source Country',
    targetCountry: 'Target Country',
    sourceLanguage: 'Source Language',
    targetLanguage: 'Target Language',
    chooseTargetLanguage: 'Choose the target language',
    targetLanguageRequired: 'Choose the target language to continue',
    autoDeleteLabel: 'Auto-delete files after',
    autoDeleteAccountDefault: 'Use account default',
    autoDeleteImmediate: 'Immediate',
    autoDelete20min: '20 minutes',
    autoDelete1h: '1 hour',
    autoDelete6h: '6 hours',
    autoDelete1d: '1 day',
    autoDelete7d: '7 days',
    autoDelete30d: '30 days',
    autoDelete90d: '90 days',
    estimatedCredits: 'Estimated Credits',
    creditCalculation: '{pages} page(s) x 1 credit(s)',
    assignToTeamMember: 'Assign to Team Member',
    notAssigned: 'Not assigned',
    notesInstructions: 'Notes / Instructions',
    teamNotesPlaceholder: 'Add instructions for the assigned team member...',
    assigning: 'Assigning...',
    analyzing: 'Analyzing...',
    assignNotify: 'Assign & Notify',
    questionOnPage: 'Page',
    questionNoPosition: 'not located',
    fileTooLargeDetail: 'The document is {size} and the limit is {max}. Split it into parts or use a lighter version and try again.',
    unsupportedFileDetail: 'Supported formats: PDF, Word (.doc, .docx) and images (JPG, PNG, TIFF, WEBP).',
    uploadingOriginal: 'Uploading the document…',
    uploadFailedMessage: 'The order was created and assigned, but the document could not be uploaded. Try again; if it keeps failing, check your connection.',
    retryUpload: 'Retry upload',
    originalUploadFailed: 'The file could not be uploaded. Check your connection and try again.',
    originalIncomplete: 'The file did not arrive complete. Please upload it again.',
    originalMissingTitle: 'Choose the file again',
    originalMissingMessage: 'We could not find this order\'s file. Choose “{name}” ({size}) again to continue; everything else you selected is kept.',
    originalMissingShort: 'This order has no file. Choose it again to continue.',
    originalDamaged: 'We could not read the file: it seems damaged or password-protected. Save it again without a password and upload it again.',
    originalDifferentFile: 'That is not this order\'s file, so a new order is started with it.',
    originalCopyPendingTitle: 'Original copy pending',
    originalCopyPending: 'We could not save a copy of the original; we are continuing with your document and will try to save it again.',
    startOrder: 'Start Order',
    analyzeContinue: 'Analyze & Continue',
    documentPreview: 'Document Preview',
    wordDocument: 'Word Document',
    contentPreview: 'Content preview:',
    noPreviewAvailable: 'No preview available',
    processing: 'Processing...',
    resumingProcessing: 'Resuming processing...',
    convertingDocument: 'Converting document...',
    generatingPreview: 'Generating preview...',
    editDocument: 'Edit Document',
    viewOriginal: 'View Original',
    saving: 'Saving...',
    backToReview: 'Back to Review',
    originalDocument: 'Original Document',
    back: 'Back',
    exitOrderTitle: 'Exit order?',
    exitOrderMessage: 'You will return to the orders list. Your progress is saved — you can resume this order at any time.',
    exitOrderConfirm: 'Yes, exit',
    exitOrderCancel: 'Stay on this order',
    documentReview: 'Document Review',
    of: 'of',
    stopAutoscroll: 'Stop auto-scroll',
    autoscroll: 'Auto-scroll',
    translating: 'Translating...',
    loadingOriginal: 'Loading original...',
    loadingShort: 'Loading...',
    downloadDocx: 'Download DOCX',
    closeOrderTitle: 'Finish the order?',
    closeOrderDesc: 'You already downloaded the laid-out document. You can mark the order as finished here, without going to translation, so it does not stay open.',
    closeOrderYes: 'Finish order',
    closeOrderNo: 'Stay in the order',
    closeOrderFailed: 'The order couldn\'t be finished. Please try again.',
    closeOrderSaving: 'Saving the document…',
    reflowPagesBtn: 'Reflow pages',
    reflowPagesError: 'Could not reflow the pages. Please try again.',
    rotatePage: "Rotate document",
    prevPage: "Previous page",
    nextPage: "Next page",
    dropHere: "Drag and drop your document here",
    clickBrowse: "or click to browse from your computer",
    selectFile: "Select File",
    supportedLabel: "Supported",
    progressMessages: {
      "Converting PDF to editable format...": "Converting PDF to editable format...",
      "Creating order...": "Creating order...",
      "Loading manuscript...": "Loading manuscript...",
      "Loading order...": "Loading order...",
      "Loading preview...": "Loading preview...",
      "Resuming processing...": "Resuming processing...",
      "Generating preview...": "Generating preview...",
      "Converting document...": "Converting document...",
      "Preparing document...": "Preparing document...",
      "Converting document to base64...": "Converting document to base64...",
      "Applying crop...": "Applying crop...",
      "Saving order details...": "Saving order details...",
      "Uploading file to cloud storage...": "Uploading file to cloud storage...",
      "Analyzing document structure...": "Analyzing document structure...",
      "Complete! Loading results...": "Complete! Loading results...",
      "Finalizing...": "Finalizing...",
      "Almost ready...": "Almost ready...",
      "Exporting images and formats...": "Exporting images and formats...",
      "Analyzing document...": "Analyzing document...",
      "OCR complete! Deducting credits...": "OCR complete! Deducting credits...",
      "Processing complete! Loading results...": "Processing complete! Loading results...",
      "Extracting text and formatting...": "Extracting text and formatting...",
      "Detecting document styles...": "Detecting document styles...",
      "Analyzing paragraphs...": "Analyzing paragraphs...",
      "Merging text blocks...": "Merging text blocks...",
      "Optimizing layout...": "Optimizing layout...",
      "Finalizing document structure...": "Finalizing document structure...",
      "OCR complete, assembling result...": "OCR complete, assembling result...",
      "Deducting credits, almost ready...": "Deducting credits, almost ready...",
    } as Record<string, string>,
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fitToWidth: 'Fit to width',
    pdfPreviewError: 'Could not load PDF preview',
    continueToTranslation: 'Continue to Translation',
    panelOriginal: 'ORIGINAL',
    panelPreview: 'PREVIEW',
    edit: 'Edit',
    previewNotAvailable: 'Preview not available',
    ocrResultsComparison: 'OCR Results - Visual Comparison',
    textBlocksDetected: 'text blocks detected',
    accuracy: 'accuracy',
    backToComparison: 'Back to Comparison',
    editTranslation: 'Edit Translation',
    translation: 'Translation',
    panelTranslation: 'TRANSLATION',
    updatingPreview: 'Updating preview...',
    translationPreviewNotAvailable: 'Translation preview not available',
    finishOrder: 'Finish Order',
    orderCompleted: 'Order Completed!',
    orderDownloaded: 'Your translated document has been downloaded.',
    orderDownloadedLayout: 'Your laid-out document has been downloaded.',
    redirecting: 'Redirecting...',
    finishOrderDesc: 'Download and complete this translation',
    finishOrderDescLayout: 'Download your laid-out document',
    document: 'Document',
    exportFormat: 'Export format',
    formatPdf: 'PDF',
    formatOdt: 'ODT (editable text)',
    translationLabel: 'Translation',
    autoLanguage: 'Auto',
    creditsToCharge: 'Credits to charge',
    creditsCharged: 'Credits used',
    currentBalanceLabel: 'Current balance: {balance} credits. Charged at OCR review.',
    credits: 'credits',
    creditBalance: 'Your balance: {balance} credits. After completion: {remaining} credits.',
    cancel: 'Cancel',
    downloadComplete: 'Download & Complete',
    insufficientCredits: 'Insufficient Credits',
    insufficientCreditsDesc: 'This order needs {required} credits and you currently have {available}. To complete it, buy more credits or upgrade your plan.',
    creditsNeeded: 'Credits needed:',
    yourBalance: 'Your balance:',
    shortfall: 'Shortfall:',
    buyCredits: 'Buy Credits',
    upgradePlan: 'Upgrade Plan',
    bestValue: 'Best value',
    contactOwner: 'Contact your account owner to add more credits.',
    preparingTexts: 'Preparing texts for translation...',
    translatingBlocks: 'Translating {count} blocks...',
    applyingTranslations: 'Applying translations...',
    includeCertificate: 'Include Certificate',
    savingDocument: 'Saving document...',
    autosavePending: 'Unsaved changes',
    autosaveSaving: 'Saving…',
    autosaveSaved: 'Saved',
    autosaveError: 'Could not save, retrying',
    editorCountWord: 'word',
    editorCountWords: 'words',
    editorCountChar: 'character',
    editorCountChars: 'characters',
    editorCountNoSpaces: 'characters without spaces',
    translatingProgress: 'Translating... ({completed}/{total})',
    generatingTranslatedPreview: 'Generating preview...',
    finalizingTranslation: 'Finalizing...',
    yes: 'Yes',
    no: 'No',
    protectPdf: 'Digital signature & protection',
    protectPdfHint: 'Disable if signing with your own token',
    outputOptions: 'Output options',
    certificateTemplate: 'Certificate',
    selectTemplate: 'Select template',
    noTemplates: 'No templates in this category',
    addTemplate: 'Manage templates',
    humanVerifiedSeal: 'Human Verified seal',
    attachOriginal: 'Attach original document',
    attachOriginalHint: 'Adds the uploaded file before or after the translation',
    attachOriginalPosition: 'Original position',
    attachOriginalAtStart: 'At the beginning',
    attachOriginalAtEnd: 'At the end',
    downloadPdf: 'Download PDF',
    downloadDoc: 'Download DOC',
    reviewDocument: 'Review Document',
    chooseFormat: 'Choose format',
    optionWord: 'Option 1',
    optionHtml: 'Option 2',
    optionMix: 'Option 3',
    optionFormat: 'Option 4',
    optionFormatHint: 'Option 2 with the order’s format template',
    optionFormatImages: 'images',
    docHeaderLabel: 'DOCUMENT HEADER — will repeat on every page of the Word/PDF',
    docFooterLabel: 'DOCUMENT FOOTER — will repeat on every page of the Word/PDF',
    panelTranscription: 'TRANSCRIPTION',
    hideEditorToolbar: 'Hide editing toolbar',
    showEditorToolbar: 'Show editing toolbar',
    verifyDocumentTitle: 'Verify document',
    verifyDocumentDesc: 'Review the original document on the left and confirm the fields on the right',
    verificationLabel: 'Verification',
    correctionPlaceholder: 'Type the correction here...',
    detectedLabel: 'Detected',
    hidePin: 'Hide pin on document',
    showPin: 'Show pin on document',
    editedIndicator: 'Edited',
    skip: 'Skip',
    confirm: 'Confirm',
    localizationNoticeTitle: 'Adapt dates and figures to the target country',
    localizationNoticeIntro: 'Target country: {country}. Dates and figures follow different conventions there — we can adjust them so they read naturally.',
    localizationNoticeSummaryTpl: 'We found {items}.',
    localizationNoticeSummaryAnd: 'and',
    localizationNoticeDatesOne: '1 date',
    localizationNoticeDatesMany: '{n} dates',
    localizationNoticeFiguresOne: '1 figure',
    localizationNoticeFiguresMany: '{n} figures',
    localizationNoticeExamplesTitle: 'How they would look',
    localizationNoticeChecking: 'Checking dates and figures…',
    localizationNoticeMoreTpl: 'and {n} more occurrences',
    localizationNoticeTimesMany: '{n} times',
    localizationNoticeTypeDate: 'Date',
    localizationNoticeTypeFigure: 'Figure',
    localizationNoticeSafeTitle: 'What is never touched:',
    localizationNoticeSafeBody: 'ID numbers, statute and case numbers, and phone numbers are copied digit by digit, exactly as they appear in the original.',
    localizationNoticeAdapt: 'Adapt',
    localizationNoticeKeep: 'Keep the original format',
    localizationNoticeRemember: "Don't ask again for this account",
    localizationNoticeRememberHint: 'Your choice becomes the account default.',
    localizationNoticeClose: 'Close',
  },
  certVars: {
    translatorName: 'Translator Name',
    translatorNameDesc: 'Full name of the translator/assigned person',
    translatorEmail: 'Translator Email',
    translatorEmailDesc: 'Email of the translator/assigned person',
    requesterName: 'Requester Name',
    requesterNameDesc: 'Name of who created the order',
    certificationDate: 'Certification Date',
    certificationDateDesc: 'Date of certification',
    orderDate: 'Order Date',
    orderDateDesc: 'Date the order was created',
    completionDate: 'Completion Date',
    completionDateDesc: 'Date the order was completed',
    sourceLanguage: 'Source Language',
    sourceLanguageDesc: 'Original document language',
    targetLanguage: 'Target Language',
    targetLanguageDesc: 'Translation target language',
    sourceCountry: 'Source Country',
    sourceCountryDesc: 'Country of the original document',
    targetCountry: 'Target Country',
    targetCountryDesc: 'Target country for translation',
    documentTitle: 'Document Title',
    documentTitleDesc: 'Name of the document',
    documentType: 'Document Type',
    documentTypeDesc: 'Category of document',
    originalFile: 'Original File',
    originalFileDesc: 'Original uploaded file name',
    pageCount: 'Page Count',
    pageCountDesc: 'Number of pages translated',
    orderNumber: 'Order Number',
    orderNumberDesc: 'The order id only (e.g. RR0124-1). Not combined with the project id.',
    projectNumber: 'Project Number',
    projectNumberDesc: 'Custom project reference if set, otherwise the project name',
    companyName: 'Company Name',
    companyNameDesc: 'Translation company/agency name',
    orderNotes: 'Order Notes',
    orderNotesDesc: 'Notes attached to the order',
    translatorPhone: 'Translator Phone',
    translatorPhoneDesc: 'Phone number from translator profile',
    associationName: 'Association',
    associationNameDesc: 'Professional association (ATA, NAJIT, etc.)',
    certificationNumber: 'Certification Number',
    certificationNumberDesc: 'Professional certification or member number',
    languagePairs: 'Language Pairs',
    languagePairsDesc: 'Translator language pairs (e.g., English → Spanish)',
    translatorAddress: 'Translator Address',
    translatorAddressDesc: 'Full address from translator profile',
    translatorCity: 'City',
    translatorCityDesc: 'City from translator address',
    translatorState: 'State',
    translatorStateDesc: 'State/province from translator address',
    translatorCountry: 'Country',
    translatorCountryDesc: 'Country from translator address',
    translatorZip: 'Zip Code',
    translatorZipDesc: 'Postal code from translator address',
  },
  auth: {
    invalidCredentials: 'Invalid email or password',
    userExists: 'An account with this email already exists',
    accountDeactivated: 'Your account has been deactivated. Please contact your administrator.',
    accountLocked: 'Account temporarily locked. Try again in {n} minute(s).',
    tooManyAttemptsLocked: 'Too many failed attempts. Account locked for {n} minutes.',
    verifyEmailTitle: 'Verify your email',
    verifyEmailDesc: 'We sent a 6-digit code to',
    verifyEmailCta: 'Verify & continue',
    verifyEmailWrong: 'Wrong email? Go back',
    welcomeBack: 'Welcome back',
    signInSubtitle: 'Sign in to continue to your dashboard',
    createAccount: 'Create your account',
    startFreeTrial: 'Start your free trial today',
    firstName: 'First name',
    firstNamePlaceholder: 'Jane',
    lastName: 'Last name',
    lastNamePlaceholder: 'Doe',
    registerAsCompany: 'Register as a company',
    companyName: 'Company name',
    companyNamePlaceholder: 'Your company name',
    companyLogo: 'Company Logo',
    emailAddress: 'Email address',
    emailPlaceholder: 'you@example.com',
    password: 'Password',
    passwordPlaceholder: 'Min. 8 characters',
    enterPassword: 'Enter your password',
    signIn: 'Sign in',
    signingIn: 'Signing in...',
    creatingAccount: 'Creating account...',
    uploadingLogo: 'Uploading logo...',
    forgotPassword: 'Forgot password?',
    noAccount: "Don't have an account?",
    createAnAccount: 'Create an account',
    alreadyHaveAccount: 'Already have an account?',
    signInLink: 'Sign in',
    continueWithGoogle: 'Continue with Google',
    continueWithLinkedIn: 'Continue with LinkedIn',
    orEmailPassword: 'or continue with',
    oauthFailed: 'Could not sign in with that account. Please try again.',
    oauthGoogleOriginError: 'Google rejected this origin ({origin}). In Google Cloud Console → Credentials → Web client, add that exact URL under Authorized JavaScript origins. A non-Web Client ID also causes Error 401.',
    authFailed: 'Authentication failed',
    connectionError: 'Connection error. Please try again.',
    tooManyRequests: 'Too many attempts. Please wait a few minutes before trying again.',
    sessionError: 'Failed to create session',
    // Password requirements
    pwdMinLength: 'Minimum 8 characters',
    pwdUppercase: 'At least one uppercase letter',
    pwdLowercase: 'At least one lowercase letter',
    pwdNumber: 'At least one number',
    pwdSpecialChar: 'At least one special character (!@#$%^&*)',
    pwdStrengthWeak: 'Weak',
    pwdStrengthFair: 'Fair',
    pwdStrengthGood: 'Good',
    pwdStrengthStrong: 'Strong',
    // Forgot password
    forgotPasswordTitle: 'Reset your password',
    forgotPasswordDesc: "Enter your email and we'll send you a verification code.",
    sendCode: 'Send Code',
    sendingCode: 'Sending...',
    verificationCode: 'Enter verification code',
    verificationCodeDesc: "We've sent a 6-digit code to",
    verifyCode: 'Verify Code',
    verifyingCode: 'Verifying...',
    resendCode: 'Resend code',
    resendIn: 'Resend in {n}s',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    confirmPasswordPlaceholder: 'Confirm your password',
    resetPassword: 'Set password',
    resettingPassword: 'Resetting...',
    passwordResetSuccess: 'Password Reset!',
    passwordResetSuccessDesc: 'Your password has been changed successfully. You can now sign in with your new password.',
    backToSignIn: 'Back to Sign In',
    passwordsNoMatch: 'Passwords do not match',
    passwordMinLength: 'Password must be at least 8 characters',
    // Inactivity
    sessionExpired: 'Session expired',
    sessionExpiredDesc: 'You were signed out due to inactivity. Please sign in again.',
    // Success
    accountCreatedSuccess: 'Account created successfully! Please sign in.',
    // Auth page chrome + mockup
    backToHome: 'Back to home',
    mockupTitleOrders: 'Manage all your orders',
    mockupDescOrders: 'Track documents, languages and statuses in one place.',
    mockupTitleEditor: 'AI-powered translation editor',
    mockupDescEditor: 'Side-by-side OCR review with real-time translation.',
    mockupTitleTeam: 'Built for teams & companies',
    mockupDescTeam: 'Manage translators, reviewers and admins together.',
    mockupTitleCredits: 'Credits & billing at a glance',
    mockupDescCredits: 'Monitor usage, purchase credits and track expenses.',
    mockupTabOrders: 'Orders',
    mockupTabEditor: 'Editor',
    mockupTabTeam: 'Team',
    mockupTabCredits: 'Credits',
    mockupNavOrders: 'Orders',
    mockupNavWorkspace: 'Workspace',
    mockupNavTeam: 'Team',
    mockupNavCredits: 'Credits',
    mockupNavSettings: 'Settings',
    mockupHeaderOrders: 'Orders',
    mockupHeaderWorkspace: 'Workspace',
    mockupHeaderCompanyDashboard: 'Company Dashboard',
    mockupHeaderCreditsBilling: 'Credits & Billing',
    mockupSearchOrders: 'Search orders...',
    mockupNewOrder: '+ New Order',
    mockupColDocument: 'Document',
    mockupColLanguage: 'Language',
    mockupColStatus: 'Status',
    mockupColDate: 'Date',
    mockupStatusCompleted: 'Completed',
    mockupStatusInReview: 'In Review',
    mockupStatusProcessing: 'Processing',
    mockupStatusPending: 'Pending',
    mockupStepUpload: 'Upload',
    mockupStepOcr: 'OCR',
    mockupStepReview: 'Review',
    mockupStepExport: 'Export',
    mockupOriginalLabel: 'Original (ES)',
    mockupTranslationLabel: 'Translation (EN)',
    mockupTotalOrders: 'Total Orders',
    mockupTeamMembers: 'Team Members',
    mockupThisMonth: 'This Month',
    mockupTeamActivity: 'Team Activity',
    mockupRoleTranslator: 'Translator',
    mockupRoleReviewer: 'Reviewer',
    mockupRoleAdmin: 'Admin',
    mockupRowOrders: 'orders',
    mockupAvailableCredits: 'Available Credits',
    mockupProPlan: 'Pro Plan',
    mockupBusinessPlan: 'Business Plan',
    mockupRecentTransactions: 'Recent Transactions',
    mockupCredits: 'credits',
  },
  activityPage: {
    title: 'Team Activity',
    subtitle: 'Track what your team members are working on',
    exportCSV: 'Export CSV',
    totalActivities: 'Total Activities',
    clients: 'Clients',
    ocrOperations: 'OCR Operations',
    translations: 'Translations',
    searchPlaceholder: 'Search by member name, order ID, client name...',
    allActions: 'All Actions',
    ocrGenerated: 'OCR Generated',
    ocrEdited: 'OCR Edited',
    translationGenerated: 'Translation Generated',
    translationEdited: 'Translation Edited',
    downloadAction: 'Download',
    statusCompleted: 'Status Completed',
    member: 'Member',
    time: 'Time',
    order: 'Order',
    action: 'Action',
    details: 'Details',
    loading: 'Loading activity...',
    noActivity: 'No activity found',
    showing: 'Showing',
    of: 'of',
    total: 'total',
    orderCreated: 'Order Created',
    ocrCompleted: 'OCR Completed',
    translationGen: 'Translation Gen',
    translationEdit: 'Translation Edit',
    processing: 'Processing',
    completed: 'Completed',
    translationReview: 'Translation Review',
    ocrReview: 'OCR Review',
    orderAssigned: 'Task Assigned',
    tasksAssigned: 'Tasks Assigned',
    allMembers: 'All Members',
    client: 'Client',
    noClient: 'N/A',
    clearDates: 'Clear dates',
    role: 'Role',
    orderTrashed: 'Moved to trash',
    orderRestored: 'Restored from trash',
    orderArchived: 'Archived',
    orderUnarchived: 'Unarchived',
    orderDeleted: 'Deleted permanently',
    orderDeletedAuto: 'Deleted permanently (automatic, 7 days)',
    trashEmptied: 'Trash emptied',
  },
  corpus: {
    title: 'My Terms',
    subtitle: 'Manage your translation terms by language and category.',
    folders: 'Folders',
    createFolder: 'New Folder',
    createCorpus: 'New Term Set',
    createError: 'Could not create the term set. Please try again.',
    glossary: 'Terms',
    documents: 'Documents',
    style: 'Style',
    knowledge: 'Knowledge Base',
    sourceTerm: 'Source Term',
    targetTerm: 'Target Term',
    context: 'Context / Notes',
    addTerm: 'Add Term',
    importCsv: 'Import CSV',
    exportCsv: 'Export CSV',
    importPreview: 'Preview',
    importConfirm: 'Import',
    importSuccess: 'imported',
    importDuplicates: 'duplicates skipped',
    importErrors: 'errors',
    importTotal: 'total entries',
    importDragOrClick: 'Drag a CSV file here or click to browse',
    importCsvFormat: 'Format: source_term, target_term, context (optional header row)',
    importing: 'Importing...',
    export: 'Export',
    terms: 'terms',
    noCorpus: 'No terms yet',
    selectCorpus: 'Select Term Set',
    formal: 'Formal',
    neutral: 'Neutral',
    plain: 'Plain',
    noFolders: 'No folders yet. Create one to organize your terms.',
    noEntries: 'No terms yet. Add your first term.',
    search: 'Search...',
    name: 'Name',
    description: 'Description',
    sourceLanguage: 'Source Language',
    targetLanguage: 'Target Language',
    customInstructions: 'Custom instructions',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    active: 'Active',
    inactive: 'Inactive',
    corpora: 'Term Sets',
    folder: 'Folder',
    back: 'Back',
    create: 'Create',
    showing: 'Showing',
    of: 'of',
    noResults: 'No results',
    confirmDeleteFolder: 'Delete this folder? Term sets inside will be moved to "No folder".',
    confirmDeleteCorpus: 'Delete this term set and all its entries? This cannot be undone.',
    confirmDeleteTerm: 'Delete this term?',
    prev: 'Prev',
    next: 'Next',
    comingSoonMilestone: 'Coming soon',
    folderPlaceholder: 'Legal, Medical, Academic...',
    corpusNamePlaceholder: 'Legal Certificates ES→EN',
    instructionsPlaceholder: 'E.g.: Use formal "usted" instead of "tú". Keep date format DD/MM/YYYY...',
    noFolder: 'No folder',
    color: 'Color',
    whatIsCorpus: 'What are My Terms?',
    whatIsCorpusDesc: "Your personal term library. Add terms, example documents and style rules so the AI translates using your vocabulary.",
    corpusInFolder: 'term sets in this folder',
    shared: 'Shared',
    category: 'Category',
    writingStyle: 'Writing style',
    formalDesc: 'Precise technical vocabulary, structured sentences.',
    neutralDesc: 'Balance between formality and accessibility.',
    plainDesc: 'Short sentences, simple vocabulary.',
    createNewCorpus: 'Create new term set',
    configureDesc: 'Configure your reference library and add your content.',
    corpusNameRequired: 'Term set name *',
    corpusNameExample: 'E.g.: Criminal Law, Cardiology, Transcripts...',
    corpusUseDesc: 'What will you use this term set for',
    exampleDocs: 'Example documents',
    exampleDocsDesc: 'Upload previous translations as a style and format reference.',
    originalDoc: 'Original',
    translatedDoc: 'Translation',
    docPairTip: 'Better results with complete pair (original + translation). You can upload just one.',
    knowledgeDesc: 'Articles, guides or reference material the AI consults when translating.',
    uploadArticle: 'Upload article',
    acceptedFormats: 'Accepted formats: PDF, DOCX, TXT. Max 10 MB per file.',
    dragFileHere: 'Drag your file here',
    tmxCsvFormat: 'TMX, CSV or Excel (Col A = original, Col B = translation)',
    addManually: 'add terms manually',
    generateAI: 'Generate with AI',
    share: 'Share',
    viewTabs: 'Tabs',
    viewScroll: 'Scroll',
    unorganized: 'Without folder',
    add: 'Add',
    styleExtracted: 'Style extracted',
    noDocuments: 'No example documents yet. Upload a pair to improve translation accuracy.',
    noKnowledge: 'No articles yet. Upload reference material for the AI to consult.',
    articles: 'articles',
    uploadPair: 'Upload pair',
    notes: 'Notes',
    found: 'found',
    sourceColumn: 'Source',
    targetColumn: 'Target',
    removeFile: 'Remove',
    glossaryFileError: 'Unsupported format. Upload your glossary as Excel, CSV, TSV or TMX.',
    glossarySpreadsheetError: 'That spreadsheet format can\'t be read. Open it and save it as .xlsx or CSV, then upload it.',
    glossaryTooManyTerms: 'This file has {count} terms and the limit per file is 100,000. Split it into several files.',
    glossaryEmptyError: 'No valid entries found in the file.',
    docFileError: 'Please upload a text-based file (PDF, DOCX, TXT).',
    knowledgeFileError: 'Please upload a text-based file (PDF, DOCX, TXT).',
    fileTooLarge: 'File must be under 10 MB.',
    uploadingGlossary: 'Importing glossary...',
    uploadingDocuments: 'Uploading documents...',
    uploadingKnowledge: 'Uploading',
    quickStartTitle: 'How it works',
    step1Title: '1. Create a Glossary',
    step1Desc: 'Add your glossary terms (CSV, TSV, or TMX) so the AI uses your preferred translations.',
    step2Title: '2. Add Example Docs',
    step2Desc: 'Upload previously translated documents so the AI learns your style and tone.',
    step3Title: '3. Translate',
    step3Desc: 'Select your glossary when creating an order. Your terms will be applied automatically.',
  },
  admin: {
    translationEngines: {
      header: 'Translation Engines',
      subtitle: 'Control which engines translate documents. Enforced server-side — the engine is actually switched/blocked, not just hidden in the UI.',
      tab: 'Translation',
      deeplLabel: 'DeepL',
      deeplDesc: 'Allow translating with DeepL (fast machine translation, no corpus glossaries). Applies only when the order has NO corpus.',
      claudeLabel: 'Claude',
      claudeDesc: 'Allow translating with Claude for orders WITHOUT a corpus (context-aware translation).',
      corpusLabel: 'Corpus → always Claude',
      corpusDesc: 'Fixed (not configurable): an order with a corpus is always translated through Claude — corpus glossaries can only run via Claude.',
      alwaysOn: 'Always on',
      bothDisabled: 'Both engines are disabled — orders WITHOUT a corpus will have no engine. (Orders with a corpus still translate via Claude.)',
      effectiveTitle: 'Effective behaviour',
      effectiveWithCorpus: 'With a corpus: Claude (corpus) only — always, even if the Claude toggle is off.',
      effectiveWithoutCorpusPrefix: 'Without a corpus:',
      noneEnabled: 'none enabled',
    },
    orderDetail: {
      back: 'Back',
      documentInfo: 'Document Info',
      category: 'Category',
      type: 'Type',
      pages: 'Pages',
      step: 'Step',
      exportFormat: 'Export Format',
      certificate: 'Certificate',
      digitalSignature: 'Digital Signature',
      humanVerified: 'Human Verified',
      attachOriginal: 'Attach Original',
      originalFile: 'Original File',
      translation: 'Translation',
      sourceLanguage: 'Source Language',
      targetLanguage: 'Target Language',
      sourceCountry: 'Source Country',
      targetCountry: 'Target Country',
      translationMethod: 'Translation Method',
      people: 'People',
      account: 'Account',
      createdBy: 'Created by',
      creditsAndDates: 'Credits & Dates',
      creditsEstimated: 'Credits Estimated',
      creditsConsumed: 'Credits Consumed',
      created: 'Created',
      updated: 'Updated',
      completed: 'Completed',
      processingTime: 'Processing Time',
      platformCostBreakdown: 'Platform Cost Breakdown',
      service: 'Service',
      usage: 'Usage',
      costUsd: 'Cost (USD)',
      total: 'Total',
      yes: 'Yes',
      no: 'No',
      mode: 'Mode',
      referenceNumber: 'Reference',
      clientName: 'Client',
      dueDate: 'Due date',
      verificationId: 'Verification ID',
      viewVerification: 'View public verification',
      humanVerifiedBy: 'Verified by',
      translationApprovedBy: 'Translation approved by',
      filesPurgedTpl: 'Files purged on {date}',
      deletionCertificateLink: 'View deletion certificate',
    },
    common: {
      systemOnline: 'System Online',
      allTime: 'All time',
      realTime: 'Real-time',
      search: 'Search',
      save: 'Save',
      cancel: 'Cancel',
      create: 'Create',
      edit: 'Edit',
      delete: 'Delete',
      close: 'Close',
      done: 'Done',
      loading: 'Loading...',
      saving: 'Saving...',
      success: 'Success',
      error: 'Error',
      confirmDelete: 'Delete this item? This action cannot be undone.',
      cannotBeUndone: 'This action cannot be undone.',
      actions: 'Actions',
      status: 'Status',
      name: 'Name',
      description: 'Description',
      title: 'Title',
      category: 'Category',
      type: 'Type',
      date: 'Date',
      language: 'Language',
      active: 'Active',
      inactive: 'Inactive',
      draft: 'Draft',
      total: 'Total',
      page: 'Page',
      of: 'of',
      previous: 'Previous',
      next: 'Next',
      showing: 'Showing',
      noResults: 'No results',
      allStatus: 'All Status',
      exportCSV: 'Export CSV',
      view: 'View',
      add: 'Add',
      remove: 'Remove',
      refresh: 'Refresh',
      copy: 'Copy',
      copied: 'Copied',
      select: 'Select',
      applying: 'Applying...',
    },
    overview: {
      title: 'Admin Dashboard',
      subtitle: 'System overview and management',
      totalOrders: 'Total Orders',
      activeAccounts: 'Active Accounts',
      totalUsers: 'Total Users',
      creditsBalance: 'Credits Balance',
      humanVerified: 'Human Verified',
      totalRevenue: 'Total Revenue',
      today: 'Today',
      thisWeek: 'This Week',
      thisMonth: 'This Month',
      platformCostsAllTime: 'Platform Costs (All Time)',
      totalCost: 'Total Cost',
      avgPerOrder: 'Avg / Order',
      avgPerPage: 'Avg / Page',
      ordersProcessed: 'Orders Processed',
      totalPages: 'Total Pages',
      costByService: 'Cost by Service',
      awsTextract: 'AWS Textract',
      googleDocAI: 'Google Document AI',
      claudeAI: 'Claude AI',
      deepl: 'DeepL',
      ocrExtraction: 'OCR extraction',
      ocrComplementary: 'OCR complementary',
      visionPostEdit: 'Vision + Post-Edit',
      translation: 'Translation',
      pages: 'Pages',
      costPerPage: 'Cost/page',
      ocrColor: 'OCR (color)',
      visionClass: 'Vision (class.)',
      postEdit: 'Post-edit',
      characters: 'Characters',
      words: 'Words',
      costPerMillion: 'Cost/1M chars',
      detailedUsage: 'Detailed Usage Breakdown',
      service: 'Service',
      purpose: 'Purpose',
      usage: 'Usage',
      cost: 'Cost (USD)',
      ocrTextExtraction: 'OCR text extraction',
      ocrColorDetection: 'OCR color detection',
      visionClassification: 'Vision classification',
      translationPostEdit: 'Translation post-edit',
      totalPlatformCost: 'Total Platform Cost',
      tokens: 'tokens',
      chars: 'chars',
      topLanguagePairs: 'Top Language Pairs',
      ordersByStatus: 'Orders by Status',
      noLanguagePairData: 'No language pair data available yet',
      percentOfOrders: '% of total orders',
      orders: 'orders',
      noOrderData: 'No order data available',
      totalOrdersLabel: 'Total orders',
      activitySummary: 'Activity Summary',
      ordersToday: 'Orders today',
      creditsConsumed: 'Credits consumed',
      completed: 'completed',
      inProgress: 'in progress',
      totalAccounts: 'total',
      newThisWeek: 'this week',
      newThisMonth: 'new this month',
      verified: 'verified',
      ofCompleted: 'of',
      sold: 'sold',
      consumed: 'consumed',
      creditsSold: 'Credits Sold',
      totalPurchased: 'Total purchased by customers',
      unusedAcrossAccounts: 'Unused credits across all accounts',
      used: 'Used',
      remaining: 'Remaining',
      available: 'Available',
      balance: 'Balance',
      imageBased: 'Image-based',
      textBased: 'Text-based',
      documents: 'documents',
      documentsByType: 'Documents by Type',
      pagesByType: 'Pages by Document Type',
      imagePages: 'Image pages',
      textPages: 'Text pages',
      noDocData: 'No document data available yet',
      noPageData: 'No page data available yet',
      activeUsers: 'active',
      teamUsers: 'team',
      memberUsers: 'members',
      statusUploaded: 'Uploaded',
      statusProcessing: 'Processing',
      statusOcrReview: 'OCR Review',
      statusTranslationReview: 'Translation Review',
      statusCompleted: 'Completed',
      plusTodayTpl: '+{n} today',
      plusThisWeekTpl: '+{n} this week',
    },
    accounts: {
      title: 'Accounts',
      subtitle: 'Manage all registered accounts',
      searchPlaceholder: 'Search accounts...',
      noAccountsFound: 'No accounts found',
      account: 'Account',
      owner: 'Owner',
      members: 'Members',
      plan: 'Plan',
      credits: 'Credits',
      created: 'Created',
      payAsYouGo: 'Pay-as-you-go',
      activate: 'Activate',
      suspend: 'Suspend',
      viewDetails: 'View Details',
      addCredits: 'Add Credits',
      statusActive: 'Active',
      statusTrial: 'Trialing',
      statusSuspended: 'Suspended',
      adjustCredits: 'Adjust Credits',
      currentBalance: 'Current balance',
      creditsAmount: 'Credits amount',
      amountPlaceholder: 'e.g. 100 (use negative to deduct)',
      amountHint: 'Positive to add, negative to deduct',
      reason: 'Reason',
      reasonPlaceholder: 'e.g. Courtesy credits, billing correction...',
      newBalance: 'New balance',
      creditsAppliedSuccess: 'credits applied successfully',
      failedToAdjust: 'Failed to adjust credits',
      networkError: 'Network error. Please try again.',
      applyCredits: 'Apply Credits',
      applying: 'Applying...',
      showing: 'Showing',
      accountsLabel: 'accounts',
      changePlan: 'Change plan',
      noPlan: 'No plan',
      assignPlan: 'Assign plan',
      selectPlan: 'Select a plan',
      billingCycle: 'Billing cycle',
      monthly: 'Monthly',
      yearly: 'Yearly',
      applyCreditsLabel: 'Add plan credits to current balance',
      currentPlan: 'Current plan',
      newPlan: 'New plan',
      planChangedSuccess: 'Plan updated successfully',
      failedToChangePlan: 'Failed to change plan',
      freelanceOnly: 'Freelancer only (1 member)',
      companyPlan: 'Company plan',
      removePlan: 'Remove plan',
      memberLimitExceeded: 'Account has more members than this plan allows',
      freeMembersKeepHint: 'Members stay on the account. Team features stay locked until they buy a paid plan.',
      freeCreditModeLabel: 'Credits when switching to Free',
      freeCreditKeepPlus5: 'Current balance + 5',
      freeCreditOnly5: 'Only 5 (remove the rest)',
      removePlanWarning: 'Cancels the Stripe subscription immediately and sets the balance to 0.',
      ownerViewHint: 'The plan also updates what the account owner sees: a team plan switches them to the team view. It applies on their next login.',
      lastActive: 'Last Active',
      never: 'Never',
      trialColumn: 'Trial',
      trialExpired: 'Expired',
      trialOneDay: '1 day',
      trialDays: n => `${n} days`,
    },
    users: {
      title: 'Users',
      subtitle: 'Manage all platform users',
      searchPlaceholder: 'Search users by name or email...',
      user: 'User',
      email: 'Email',
      accountLabel: 'Account',
      userType: 'Type',
      role: 'Role',
      lastLogin: 'Last Login',
      status: 'Status',
      noUsersFound: 'No users found',
      allTypes: 'All Types',
      allRoles: 'All Roles',
      typeAdmin: 'Admin',
      typeTeam: 'Team',
      typeMember: 'Member',
      typeUser: 'User',
      roleOwner: 'Owner',
      roleAdmin: 'Admin',
      roleManager: 'Manager',
      roleMember: 'Member',
      roleFreelancer: 'Freelancer',
      activate: 'Activate',
      deactivate: 'Deactivate',
      impersonate: 'Impersonate',
      viewActivity: 'View activity',
      never: 'Never',
      showing: 'Showing',
      usersLabel: 'users',
      subtitleActivity: 'Monitor user activity and engagement',
      exportExcel: 'Export to Excel',
      exportEmail: 'Email',
      exportPlan: 'Plan',
      exportCreditsAvailable: 'Credits available',
      exportConsumedAccount: 'Consumed (account)',
      exportConsumedUser: 'Consumed (user)',
      onlineNow: 'Online Now',
      activeToday: 'Active Today',
      byRole: 'By Role',
      teamShort: 'team',
      memberShort: 'member',
      userShort: 'user',
      allActivity: 'All Activity',
      filterOnline: 'Online Now',
      filterRecent: 'Recently Active',
      filterActiveStatus: 'Status: Active',
      filterSuspendedStatus: 'Status: Suspended',
      statusOnline: 'Online',
      statusRecently: 'Recently',
      statusOffline: 'Offline',
      statusSuspendedBadge: 'Suspended',
      lastActive: 'Last Active',
      lastPage: 'Last Page',
      joined: 'Joined',
      noAccount: 'No account',
      moreAccountsTpl: '+{n} more',
      showingRangeTpl: 'Showing {from}-{to} of {total} users',
      timeJustNow: 'just now',
      timeMinAgoTpl: '{n}m ago',
      timeHourAgoTpl: '{n}h ago',
      timeDayAgoTpl: '{n}d ago',
      timeWeekAgoTpl: '{n}w ago',
      platformAdminLabel: 'Platform admin',
      platformAdminBadge: 'Platform admin',
      platformAdminDescription: 'Grants access to /admin without changing their team or plan role. They can switch views like ambassadors.',
      platformAdminReloginHint: 'They must sign out and sign in again for the change to take effect.',
      platformAdminGrant: 'Make platform admin',
      platformAdminRevoke: 'Remove platform admin',
      platformAdminGranted: 'Platform admin granted',
      platformAdminRevoked: 'Platform admin removed',
      platformAdminFailed: 'Could not update platform admin',
      platformAdminConfirmGrant: 'Grant platform admin to this user? They keep their team/plan view and must re-login.',
      platformAdminConfirmRevoke: 'Remove platform admin from this user? They must re-login.',
    },
    orders: {
      title: 'All Orders',
      subtitle: 'View and manage all orders across accounts',
      searchPlaceholder: 'Search orders by number or document name...',
      totalOrders: 'Total Orders',
      completed: 'Completed',
      inProgress: 'In Progress',
      totalPlatformCost: 'Total Platform Cost',
      orderNum: 'Order #',
      document: 'Document',
      account: 'Account',
      pages: 'Pages',
      credits: 'Credits',
      quality: 'Quality',
      cost: 'Cost (USD)',
      status: 'Status',
      date: 'Date',
      actions: 'Actions',
      viewBtn: 'View',
      logsBtn: 'Logs',
      noOrdersFound: 'No orders found.',
      platformCostBreakdown: 'Platform Cost Breakdown',
      awsTextract: 'AWS Textract',
      googleDocAI: 'Google Doc AI',
      claudeAI: 'Claude AI',
      deepl: 'DeepL',
      grandTotal: 'Grand Total',
      allServicesCombined: 'all services combined',
      detailedUsageBreakdown: 'Detailed Usage Breakdown',
      allTimeAggregated: 'All-time aggregated usage across all orders',
      service: 'Service',
      purpose: 'Purpose',
      usage: 'Usage',
      costUsd: 'Cost (USD)',
      ocrTextExtraction: 'OCR (text extraction)',
      ocrComplementary: 'OCR (complementary)',
      visionClassification: 'Vision (classification)',
      translationClaude: 'Translation (Claude)',
      translation: 'Translation',
      totalLbl: 'Total',
      orderLogsTitle: 'Order Logs',
      loadingLogs: 'Loading logs...',
      noLogsFound: 'No logs found for this order.',
      timestamp: 'Timestamp',
      action: 'Action',
      creditsCol: 'Credits',
      pagesLower: 'pages',
      charsLower: 'chars',
      tokensLower: 'tokens',
      statusUploaded: 'Uploaded',
      statusProcessing: 'Processing',
      statusOcrReview: 'OCR Review',
      statusTranslationReview: 'Translation Review',
      statusCompleted: 'Completed',
      allStatus: 'All Status',
      actionOrderCreated: 'Order Created',
      actionOcrCompleted: 'OCR Completed',
      actionOcrGenerated: 'OCR Generated',
      actionOcrEdited: 'OCR Edited',
      actionTranslationGen: 'Translation Gen',
      actionTranslationEdit: 'Translation Edit',
      actionDownload: 'Download',
      actionProcessing: 'Processing',
      actionCompleted: 'Completed',
      actionTranslationReview: 'Translation Review',
      actionOcrReview: 'OCR Review',
      exportCSV: 'Export CSV',
      wordsLower: 'words',
      ordersLower: 'orders',
      showingRangeTpl: 'Showing {from}-{to} of {total} orders',
      orderLogsForTpl: 'Order Logs — {orderNumber}',
      visibilityAll: 'Visibility: all',
      visibilityActive: 'Active',
      visibilityTrash: 'In trash',
      visibilityDeleted: 'Deleted permanently',
      visibilityArchived: 'Archived',
      badgeInTrash: 'In trash',
      badgeDeleted: 'Deleted permanently',
      deletedAuto: 'Automatic (7 days in trash)',
      byTpl: '{date} · by {name}',
      inTrashSince: 'In trash since',
      statusCancelled: 'Cancelled',
      actorAuto: 'Automatic',
      userCol: 'User',
    },
    waitlist: {
      title: 'Waitlist',
      subtitle: 'Founding User signups from the landing page',
      totalSignups: 'Total Signups',
      companies: 'Companies',
      wantBetaAccess: 'Want Beta Access',
      latestSignup: 'Latest Signup',
      searchPlaceholder: 'Search by name, email, company, or role...',
      noEntriesFound: 'No waitlist entries found',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      role: 'Role',
      pagesPerWeek: 'Pages/Wk',
      biggestPain: 'Biggest pain point',
      priceWillingness: 'Price willingness',
      betaInterest: 'Beta Interest',
      language: 'Language',
      signedUp: 'Signed Up',
      signedUpAt: 'Signed up at',
      deleteEntry: 'Delete entry',
      viewDetails: 'View details',
      deleteConfirm: 'Delete this entry?',
    },
    analytics: {
      title: 'Analytics',
      subtitle: 'Platform performance and insights',
      totalOrders: 'Total Orders',
      activeAccounts: 'Active Accounts',
      ordersProcessed: 'Orders Processed',
      creditsConsumed: 'Credits Consumed',
      ordersByStatus: 'Orders by Status',
      topLanguagePairs: 'Top Language Pairs',
      documentsByType: 'Documents by Type',
      pagesByDocType: 'Pages by Document Type',
      imageBased: 'Image-based',
      textBased: 'Text-based',
      imagePages: 'Image pages',
      textPages: 'Text pages',
      documents: 'documents',
      pages: 'pages',
      noOrderData: 'No order data available yet',
      noLanguagePairData: 'No language pair data available yet',
      noDocData: 'No document data available yet',
      noPageData: 'No page data available yet',
      statusUploaded: 'Uploaded',
      statusProcessing: 'Processing',
      statusOcrReview: 'OCR Review',
      statusTranslationReview: 'Translation Review',
      statusCompleted: 'Completed',
    },
    blog: {
      title: 'Blog · Ranqio Articles',
      subtitle: 'Content ingested via API + SEO, OG, JSON-LD and status editing.',
      searchPlaceholder: 'Search by title...',
      allStatuses: 'All statuses',
      statusDraft: 'Draft',
      statusScheduled: 'Scheduled',
      statusPublished: 'Published',
      statusArchived: 'Archived',
      noArticles: 'No articles.',
      titleCol: 'Title',
      status: 'Status',
      locale: 'Locale',
      author: 'Author',
      publishedAt: 'Published',
      actions: 'Actions',
      viewOnSite: 'View on site',
      editSeo: 'Edit SEO',
      deleteArticle: 'Delete',
      deleteConfirm: 'Delete this article? This action cannot be undone.',
      articles: 'articles',
      pageOf: 'Page {p} of {t}',
      errorSave: 'Error saving.',
    },
    copy: {
      title: 'Site copy',
      subtitle: 'Edit the main landing page text in all languages. Changes live in draft until you hit Publish.',
      discardDraft: 'Discard draft',
      saveDraft: 'Save draft',
      publish: 'Publish',
      publishing: 'Publishing',
      draftSaved: '✓ Draft saved. Not visible to the public yet.',
      publishedMsg: '🚀 Published. Visitors now see the changes.',
      errorSaveDraft: 'Error saving draft',
      errorPublish: 'Error publishing',
      discardConfirm: 'Discard draft and revert to the published version?',
      draftDiscarded: 'Draft discarded',
      loadingError: 'Error loading copy',
      unpublishedChanges: 'There are unpublished changes in',
      pressPublish: 'Press Publish so visitors see them.',
      unsaved: 'unsaved',
      draftBadge: 'draft',
      pendingChanges: 'Unpublished changes',
    },
    nav: {
      title: 'Navigation menu',
      subtitle: 'Configure the header items. You can create links to pages, dropdowns with sub-items, or external links.',
      newItem: 'New item',
      noItems: 'No items yet. Create the first one to show it in the site header.',
      dragHint: '💡 Drag items by the handle on the left to reorder them.',
      itemCreated: 'Item created',
      itemUpdated: 'Item updated',
      saveError: 'Error saving',
      deleteConfirm: 'Delete this item? Its children will also be deleted.',
      newItemTitle: 'New item',
      editItemTitle: 'Edit item',
      kind: 'Type',
      kindLink: 'External link (URL)',
      kindLanding: 'Landing page (built in EZDocuAI)',
      kindDropdown: 'Dropdown (with sub-items)',
      url: 'URL',
      urlHint: 'E.g.: https://docs.example.com or /pricing',
      landingPage: 'Landing page',
      landingHint: 'If the landing is renamed or deleted, the link updates/deactivates automatically.',
      selectPlaceholder: '— Select —',
      parentItem: 'Parent item (optional)',
      parentHint: 'To nest inside a dropdown',
      noParent: '— No parent (top-level) —',
      activeLabel: 'Active (visible in header)',
      addSubItem: 'Add sub-item',
      dragReorder: 'Drag to reorder',
      deleteItem: 'Delete',
      inactive: 'inactive',
      items: 'items',
    },
    seo: {
      title: 'SEO and Metadata',
      subtitle: 'Optimize your site for search engines and social networks.',
      tabGeneral: 'General',
      tabRobots: 'Robots',
      tabSitemap: 'Sitemap',
      tabPages: 'Pages',
      errorLoading: 'Error loading settings',
      settingsSaved: 'Settings saved',
      errorSaving: 'Error saving',
      saveChanges: 'Save changes',
      addSocialProfile: 'Add social profile',
      generalSection: 'General settings',
      siteName: 'Site name',
      siteNamePlaceholder: 'EZDocuAI',
      siteUrl: 'Site URL',
      siteUrlPlaceholder: 'https://www.ezdocu.ai',
      titleTemplate: 'Title template',
      titleTemplateHint: 'Use %s where each page title will go',
      titleTemplatePlaceholder: '%s | EZDocuAI',
      defaultTitle: 'Default title',
      titleCounterLabel: 'Title',
      defaultDescription: 'Default description',
      descriptionCounterLabel: 'Description',
      ogImage: 'Social media image (OG)',
      ogImageHint: 'Recommended size: 1200×630px. Upload from your machine or paste a URL.',
      ogImagePlaceholder: 'https://… or upload an image',
      googleVerification: 'Google verification code',
      googleVerificationPlaceholder: 'google-site-verification=...',
      socialProfiles: 'Social profiles',
      socialProfilePlaceholder: 'https://www.instagram.com/ezdocu',
      googlePreview: 'Google preview',
      socialPreview: 'Social media preview',
      indexingSection: 'Site indexing',
      allowGoogleIndex: 'Allow Google to index the site',
      allowGoogleIndexHint: 'If disabled, the site will not appear in search engines.',
      blockedPathsSection: 'Paths blocked for crawlers',
      blockedPathsHint: 'These paths will not be crawled by Google or other search engines.',
      blockedPathPlaceholder: '/path-to-block/',
      aiPolicySection: 'AI crawler policy',
      aiPolicyAllowAll: 'Allow all',
      aiPolicyAllowAllDesc: 'Google, Bing, ChatGPT, Claude can access',
      aiPolicyBlockTraining: 'Block training',
      aiPolicyBlockTrainingDesc: 'Allow AI search, block training (GPTBot, CCBot)',
      aiPolicyBlockAll: 'Block all AI bots',
      aiPolicyBlockAllDesc: 'Only allow traditional search engines',
      robotsPreviewSection: 'robots.txt preview',
      sitemapSection: 'Sitemap settings',
      autoGenerateSitemap: 'Auto-generate sitemap',
      autoGenerateSitemapHint: 'Includes static pages, active landing pages and CMS content.',
      sitemapUpdated: 'Sitemap updated',
      sitemapUrlCopied: 'URL copied',
      includedContentSection: 'Included content',
      includedStaticPages: 'Static pages (home, pricing, blog)',
      includedLandingPages: 'Active dynamic landing pages',
      includedBlogArticles: 'Published blog articles',
      excludedHint: 'Private pages (dashboard, workspace) are excluded automatically.',
      metaBasicSection: 'Basic meta',
      metaTitleLabelTpl: 'Meta title ({n}/60 recommended)',
      metaDescriptionLabelTpl: 'Meta description ({n}/155 recommended)',
      focusKeyword: 'Focus keyword',
      canonicalUrl: 'Canonical URL',
      canonicalPlaceholder: 'https://www.ezdocu.ai/...',
      noindexLabel: 'noindex',
      nofollowLabel: 'nofollow',
      openGraphSection: 'Open Graph',
      ogTitle: 'OG title',
      ogDescription: 'OG description',
      ogImageUrl: 'OG image URL',
      ogImageUrlPlaceholder: 'https://...',
      twitterSection: 'Twitter Cards',
      twitterCard: 'Twitter card',
      twitterSite: 'Twitter @site',
      twitterSitePlaceholder: '@ezdocuai',
      twitterTitle: 'Twitter title',
      twitterDescription: 'Twitter description',
      twitterImage: 'Twitter image URL',
      jsonLdSection: 'JSON-LD',
      jsonLdLabel: 'Schemas (array of { type, jsonLd })',
      jsonLdHint: 'Injected inside <head> when the page renders.',
      jsonLdInvalid: 'Invalid JSON-LD',
      changesPublished: 'Changes published',
      previewGoogle: 'Google preview',
      previewOg: 'OG preview',
      infoSection: 'Info',
      pageKeyLabel: 'page_key',
      lastEdited: 'Last edited',
    },
    apiKeys: {
      title: 'API Keys',
      subtitle: 'Manage access keys for external integrations (Ranqio, etc.). You can configure a webhook to receive confirmation when EZDocuAI processes an article.',
      noKeys: 'No API keys created yet.',
      webhookDeleted: 'Webhook deleted',
      webhookSaved: 'Webhook saved',
      saveError: 'Error saving',
      copiedToClipboard: '✓ Copied to clipboard',
      statusActive: 'active',
      statusRevoked: 'revoked',
      webhookBadge: 'webhook',
      rateLimitTpl: '{n}/min',
      lastUsedTpl: 'last used: {when}',
      lastUsedNever: 'never',
      lastDeliveryTpl: 'last delivery: HTTP {status}',
      noDeliveriesYet: 'no deliveries yet',
      editWebhook: 'Edit webhook',
      configureWebhook: 'Configure webhook',
      webhookUrlLabel: 'Webhook URL',
      webhookUrlPlaceholder: 'https://your-domain.com/webhooks/ezdocu',
      webhookUrlHint: 'EZDocuAI will send a signed POST here whenever an article is created, updated, or deleted from this API key.',
      secretLabel: 'Secret (optional)',
      secretPlaceholder: 'Auto-generated if left empty',
      secretHint: 'Used to sign each request (HMAC-SHA256) in the X-EZDocu-Signature header.',
      showSecret: 'Show',
      hideSecret: 'Hide',
      saveSecretWarning: '⚠ Save this secret — it will not be shown again',
      deleteWebhook: 'Delete webhook',
      cancel: 'Cancel',
      save: 'Save',
      payloadExample: 'Webhook payload (example)',
    },
    landingPages: {
      title: 'Landing Pages',
      subtitle: 'Manage dynamic landing pages: offers, promotions, events.',
      newPage: 'New page',
      noPages: 'No landing pages yet',
      createFirst: 'Create the first',
      deleteConfirm: 'Delete this landing page? This action cannot be undone.',
      totalLbl: 'Total',
      activeLbl: 'Active',
      draftsLbl: 'Drafts',
      titleCol: 'Title',
      slug: 'Slug',
      status: 'Status',
      updated: 'Updated',
      actions: 'Actions',
      activeBadge: 'Active',
      draftBadge: 'Draft',
      viewSite: 'View',
      editPage: 'Edit',
      deletePage: 'Delete',
      slugTitleRequired: 'Slug and title are required',
      errorCreate: 'Error creating',
      errorSave: 'Error saving',
      errorPublish: 'Error publishing',
      draftSavedMsg: '✓ Draft saved. Not yet visible to the public.',
      publishedMsg: '🚀 Published. Visitors can now see the changes.',
      draftDiscarded: 'Draft discarded',
      draftUnpublished: 'Unpublished draft',
      errorCreatePage: 'Error creating the page',
      errorSaveDraft: 'Error saving the draft',
    },
    templates: {
      title: 'Certificate Templates',
      subtitle: 'Manage global certificate templates available to all accounts.',
      noTemplates: 'No templates yet',
      uploadTemplate: 'Upload template',
      newTemplate: 'New template',
      editTemplate: 'Edit template',
      activateTemplate: 'Activate',
      deactivateTemplate: 'Deactivate',
      default: 'Default',
      global: 'Global',
      custom: 'Custom',
      uploadDocx: 'Upload DOCX',
      globalTemplatesStat: 'Global Templates',
      activeStat: 'Active',
      defaultStat: 'Default',
      inactiveStat: 'Inactive',
      categoryGeneral: 'General',
      categoryLegal: 'Legal',
      categoryImmigration: 'Immigration',
      categoryAcademic: 'Academic',
      allTemplatesFilter: 'All Templates',
      colTemplate: 'Template',
      colCategory: 'Category',
      colAccount: 'Account',
      colFlags: 'Flags',
      colCreated: 'Created',
      colActions: 'Actions',
      colActive: 'Active',
      loadingTemplates: 'Loading templates...',
      noTemplatesFound: 'No templates found.',
      flagDefault: 'Default',
      flagGlobal: 'Global',
      editBtn: 'Edit',
      newGlobalTemplate: 'New Global Template',
      uploadTemplateDialog: 'Upload Template',
      nameLabel: 'Name',
      namePlaceholder: 'e.g., Certificate of Translation',
      descriptionLabel: 'Description (optional)',
      descriptionPlaceholder: 'Brief description of this template',
      categoryLabel: 'Category',
      languageLabel: 'Language',
      anyLanguage: 'Any Language',
      languageEnglish: 'English',
      languageSpanish: 'Spanish',
      languagePortuguese: 'Portuguese',
      cancelBtn: 'Cancel',
      createAndEdit: 'Create & Edit',
      creatingBtn: 'Creating...',
      editingGlobalTemplate: 'Editing global template',
      variablesBtn: 'Variables',
      clickToCopyHint: 'Click to copy → Ctrl+V',
      copiedTooltip: '✓ Copied!',
      clickToCopyTooltip: 'Click to copy → {description}',
      saveAndClose: 'Save & Close',
      savingBtn: 'Saving...',
      variableTranslatorName: 'Full name of the translator',
      variableTranslatorEmail: 'Translator email address',
      variableCertificationDate: 'Date of certification',
      variableOrderDate: 'Date the order was created',
      variableCompletionDate: 'Date the order was completed',
      variableSourceLanguage: 'Original document language',
      variableTargetLanguage: 'Translation language',
      variableSourceCountry: 'Country of origin',
      variableTargetCountry: 'Target country',
      variableDocumentTitle: 'Title of the document',
      variableDocumentType: 'Category/type of document',
      variablePageCount: 'Number of pages',
      variableOrderNumber: 'Order reference number',
      variableProjectNumber: 'Project reference number',
      failedToPrepareTpl: 'Failed to prepare template: {message}',
      failedToSaveTpl: 'Failed to save template: {message}',
      failedToSaveGeneric: 'Failed to save template. Check console for details.',
      failedToCreate: 'Failed to create template',
    },
  },
  adminPayments: {
    title: 'Payments',
    transactionsOne: '{n} transaction',
    transactionsOther: '{n} transactions',
    subtitle: 'plan purchases, credit packs, renewals and auto-recharges',
    refresh: 'Refresh',
    exportExcel: 'Export to Excel',
    exporting: 'Exporting…',
    exportError: 'Export failed. Please try again.',
    searchPlaceholder: 'Search by customer, email, account, plan or Stripe ID…',
    countOf: '{n} of {total}',
    loading: 'Loading…',
    empty: 'No payments recorded yet.',
    emptyFiltered: 'No payments match these filters.',
    viewAccount: 'View account',
    staleBackend: 'The server does not apply these filters yet: the table only filters the 200 most recent payments and the totals cover all payments. The Excel file is complete and filtered.',
    filters: {
      period: 'Period',
      type: 'Type',
      status: 'Status',
      from: 'From',
      to: 'To',
      clear: 'Clear filters',
      sinceDate: 'Since {date}',
      untilDate: 'Until {date}',
    },
    periods: {
      all: 'All time',
      thisMonth: 'This month',
      lastMonth: 'Last month',
      last30Days: 'Last 30 days',
      thisYear: 'This year',
      custom: 'Custom',
    },
    categories: {
      all: 'All',
      plan: 'Plans',
      credits: 'Credit packs',
      autoRecharge: 'Auto-recharges',
      refund: 'Refunds',
    },
    statusAll: 'All',
    statuses: {
      active: 'Active',
      trial: 'Trial',
      cancelsAtEnd: 'Cancelling',
      canceled: 'Cancelled',
      unpaid: 'Past due',
      incomplete: 'Incomplete',
      unknown: 'No data',
      paid: 'Paid',
      refund: 'Refund',
    },
    statusHelp: {
      active: 'Active subscription: it will renew automatically.',
      cancelsAtEnd: 'Cancelled by the customer: it won\'t renew at the end of the period.',
      unpaid: 'Stripe could not charge the renewal.',
      unknown: 'Stripe could not be reached; the next charge is an estimate.',
    },
    cycles: {
      yearly: 'Yearly',
      monthly: 'Monthly',
    },
    items: {
      plan: 'Plan',
      creditPack: 'Credit pack',
      autoRecharge: 'Auto-recharge',
      refund: 'Refund',
    },
    cards: {
      revenue: 'Revenue',
      total: 'Total',
      netAndRefunds: 'Net {net} · refunds {refunds}',
      customersOne: '{n} customer',
      customersOther: '{n} customers',
      thisMonth: 'This month',
      plans: 'Plans',
      paymentsOne: '{n} payment',
      paymentsOther: '{n} payments',
      creditPacks: 'Credit packs',
      purchasesOne: '{n} purchase',
      purchasesOther: '{n} purchases',
      activeSubscriptions: 'Active subscriptions',
      today: 'Today',
      mrr: 'MRR {usd}',
      cancelling: '{n} cancelling',
      unpaid: '{n} past due',
      unknown: '{n} with no data',
      creditsSold: 'Credits sold',
    },
    columns: {
      date: 'Date',
      customer: 'Customer',
      account: 'Account',
      item: 'Item',
      cycle: 'Cycle',
      status: 'Status',
      nextCharge: 'Next charge',
      description: 'Description',
      credits: 'Credits',
      amount: 'Amount',
      stripeId: 'Stripe ID',
      mode: 'Mode',
    },
    excel: {
      sheet: 'Payments',
      date: 'Date',
      customer: 'Customer',
      email: 'Email',
      account: 'Account',
      type: 'Type',
      cycle: 'Cycle',
      status: 'Status',
      nextCharge: 'Next charge',
      description: 'Description',
      credits: 'Credits',
      amountUsd: 'Amount USD',
      stripeId: 'Stripe ID',
      mode: 'Mode',
    },
  },
  glossary: {
    title: 'Global glossary',
    subtitle: 'Terminology shared across all accounts. Used in automatic translations.',
    categoriesBtn: 'Categories',
    uploadFile: 'Upload file',
    newTerm: 'New term',
    breadcrumbRoot: 'Glossary',
    categoriesWithDataTpl: 'Categories with terms ({n})',
    emptyCategoriesTpl: 'Empty categories ({n})',
    termsCountOne: '{n} term',
    termsCountOther: '{n} terms',
    pairsCountOne: '{n} pair',
    pairsCountOther: '{n} pairs',
    defsCountTpl: '{n} def.',
    backToCategories: 'Categories',
    pairType: 'Bilingual pair',
    definitionType: 'Monolingual (definition)',
    pairTypeDescTpl: 'Term + translation. {n} entries.',
    definitionTypeDescTpl: 'Term + definition. {n} entries.',
    backToTypes: 'Types',
    langComboTermsTpl: '{n} terms · {combo}',
    emptyType: 'No entries in this type.',
    deleteLangTitle: 'Delete terms',
    deleteLangBodyTpl: 'You are about to delete {n} terms from {cat} · {combo}.',
    deleteCannotUndo: 'This action cannot be undone.',
    cancel: 'Cancel',
    deleteCountTpl: 'Delete {n}',
    backToLanguages: 'Languages',
    filterByLetter: 'Filter by letter',
    allLetters: 'All',
    search: 'Search',
    searchPlaceholder: 'Search term or translation...',
    onlyVerified: 'Only verified',
    refresh: 'Refresh',
    downloadPdf: 'Download PDF',
    generatingPdf: 'Generating…',
    downloadPdfTipFiltered: 'Download respects active filters',
    downloadPdfTipAll: 'Download the ENTIRE glossary of this category/type',
    selectFilterCountTpl: 'Select all {n} terms in filter',
    selectedCountTpl: '{n} selected',
    allFilter: '(entire filter)',
    verify: 'Verify',
    unverify: 'Unverify',
    clear: 'Clear',
    pageXofYTpl: 'page {p} / {t}',
    letterLabelTpl: 'letter {l}',
    otherLetter: 'other',
    noTerms: 'No terms match these filters.',
    previous: '← Previous',
    next: 'Next →',
    term: 'Term',
    translation: 'Translation',
    definition: 'Definition',
    source: 'Source',
    actions: 'Actions',
    verifiedTitle: 'Verified · click to unverify',
    unverifiedTitle: 'Unverified · click to approve',
    selectPage: 'Select this page',
    entryNewTitle: 'New term',
    entryEditTitle: 'Edit term',
    fieldCategory: 'Category',
    fieldCategoryHelp: 'Groups terms by domain (legal, medical, technical...). Used to suggest the right glossary when translating a document.',
    fieldType: 'Type',
    fieldTypeHelp: 'Translation pair = source term + target term in another language. Definition (monolingual) = term + explanation in the same language.',
    fieldSourceTerm: 'Source term',
    fieldSourceTermHelp: 'The word or phrase as it appears in the original document. Stored lowercase for case-insensitive search.',
    fieldLang: 'Language',
    fieldSourceLangHelp: 'Language of the source term in ISO-639-1 code (es, en, pt, fr, it, de).',
    fieldTranslation: 'Translation',
    fieldTranslationHelp: 'How the source term must be translated into the target language. Used as preferred reference.',
    fieldTargetLangHelp: 'Language of the translated term (ISO-639-1).',
    fieldDefinition: 'Definition',
    fieldDefinitionHelp: 'Explanation of the term in the same language as the source. Useful for technical/legal terms without direct translation.',
    fieldContext: 'Context (optional)',
    fieldContextHelp: 'Notes about when this term applies (country, subdomain, register). Reviewer guidance only.',
    fieldPriority: 'Priority (0-100)',
    fieldPriorityHelp: 'Among duplicate terms, higher priority wins. 0 = normal, 50+ = preferred, 100 = always force.',
    fieldVerified: 'Verified',
    fieldVerifiedHelp: 'Marks human-reviewed terms. Verified entries take priority over unverified ones when translating.',
    fieldSourceKind: 'Source type',
    fieldSourceKindHelp: 'Where the term comes from: Manual, PDF/CSV/TXT, AI (Claude), Web (public page), Client (private document).',
    fieldSourceLabel: 'Name / reference',
    fieldSourceLabelHelp: 'Human-friendly name for audit. Shown in the Source column of the table.',
    fieldSourceUrl: 'URL (if from web)',
    fieldSourceUrlHelp: 'Canonical link to the original page. If empty no link is shown in the table.',
    fieldSourceUrlHint: 'E.g.: https://www.justicia.ar/herramientas/glosario',
    provenanceTitle: 'Source / Provenance',
    selectPlaceholder: '— Select —',
    entryPairOption: 'Translation pair',
    entryDefinitionOption: 'Definition (monolingual)',
    save: 'Save',
    sourceTermPlaceholder: 'E.g.: birth certificate',
    translationPlaceholder: 'E.g.: acta de nacimiento',
    definitionPlaceholder: 'Definition of the term in the same source language...',
    contextPlaceholder: 'E.g.: used in legal documents in Colombia',
    sourceLabelPlaceholder: 'E.g.: Cabanellas 2023 / Client ACME',
    originalFile: 'Original file',
    sourceManual: 'Manual',
    sourcePdf: 'PDF',
    sourceCsv: 'CSV',
    sourceTxt: 'TXT',
    sourceAi: 'AI',
    sourceLearning: 'Translation',
    sourceWeb: 'Web',
    sourceClient: 'Client',
    uploadTitle: 'Upload glossary',
    uploadConfirmTitle: 'Confirm data',
    tabFile: 'File',
    tabUrl: 'URL',
    tabPaste: 'Paste text',
    uploadClickToSelect: 'Click to select file',
    uploadHint: 'PDF, CSV or TXT · no size limit',
    uploadAnalyzing: 'Claude is analyzing the file…',
    urlFieldLabel: 'Glossary URL',
    urlFieldHint: 'E.g.: https://www.justicia.ar/herramientas/glosario',
    analyzeUrl: 'Analyze URL',
    readingPage: 'Reading page…',
    pageTitleLabel: 'Title',
    pasteFieldLabel: 'Content (paste from Claude Max, Excel, web, etc.)',
    pasteFieldHint: 'Detected as CSV if it has commas + newlines; otherwise processed as free text',
    pastePlaceholder: 'source_term,source_lang,target_term,target_lang,entry_type\nbirth certificate,en,acta de nacimiento,es,pair\n...',
    sourceName: 'Source name',
    sourceNamePlaceholder: 'E.g.: Claude Max — Legal glossary',
    originalUrlOptional: 'Original URL (optional)',
    analyzeText: 'Analyze text',
    analyzing: 'Analyzing…',
    claudeHelpTitle: '💡 What Claude does on upload:',
    claudeHelpDetectLangs: 'Detects languages (source / target)',
    claudeHelpDetectType: 'Detects type (bilingual pair or monolingual)',
    claudeHelpSuggestCategory: 'Suggests an appropriate category',
    claudeHelpSaveSource: 'Saves the source (file / URL / label) on every term',
    claudeAnalyzedTpl: 'Claude analyzed {file} ({chars} chars). Review and edit anything that doesn\'t fit.',
    destCategory: 'Destination category',
    contentType: 'Content type',
    pairOption: 'Bilingual pair',
    monolingualOption: 'Monolingual',
    srcLang: 'Source language',
    tgtLang: 'Target language',
    savedSource: 'Source saved on every term',
    changeSource: '← Change source',
    process: 'Process',
    urlInvalid: 'Invalid URL',
    urlEmpty: 'Enter a URL',
    urlNoText: 'The page did not return enough text',
    pasteTooShort: 'Paste at least 200 characters of content',
    urlErrorGeneric: 'Error analyzing URL',
    detectError: 'Could not detect metadata',
    uploadErrorGeneric: 'Upload error',
    confSure: 'sure',
    confProbable: 'likely',
    confUnsure: 'uncertain',
    msgTermCreated: 'Term created',
    msgTermUpdated: 'Term updated',
    msgTermDeleted: 'Term deleted',
    msgSaveError: 'Save error',
    msgBulkVerifiedTpl: '{n} terms verified',
    msgBulkUnverifiedTpl: '{n} terms unverified',
    msgVerifyError: 'Error verifying',
    msgBulkDeletedTpl: '{n} terms deleted',
    msgDeleteError: 'Error deleting',
    msgPdfDownloadedTpl: 'PDF downloaded ({n} entries)',
    msgPdfError: 'Error exporting PDF',
    errorFetchEntries: 'Error fetching entries',
    helpAria: 'Help',
    langNameEs: 'Spanish',
    langNameEn: 'English',
    langNamePt: 'Portuguese',
    langNameFr: 'French',
    langNameIt: 'Italian',
    langNameDe: 'German',
  },
  sharedComponents: {
    notificationBell: {
      ariaLabel: 'Notifications',
    },
    languageSelector: {
      tooltip: 'Change language',
    },
    professionalProfile: {
      associationSearchHint: 'Search 85+ verified associations or type "Other"',
      associationSearchPlaceholder: 'Search association (e.g. ATA, BDÜ, CTPCBA...)',
      noResults: 'No results',
    },
  },
  verifyPublic: {
    humanVerified: 'Human verified',
    kmsSigned: 'KMS signed',
    translationOnRecord: 'Translation on record',
    statusHashing: 'Verifying file…',
    statusPurgedWithHash: 'This translation was completed and signed by the translator above. The original file is no longer stored on our servers, but you can upload your copy below to verify the signature matches — the file is checked in your browser and never leaves your device.',
    statusWithHash: 'This translation was completed and signed by the translator above. To confirm the PDF you received is authentic, upload it below — the file is checked in your browser and never leaves your device.',
    statusNoHash: 'This translation was completed and signed by the translator above. The signed content is stored on our servers and has not been altered.',
    documentVerifiedTitle: 'Document verified',
    documentVerifiedDesc: 'The uploaded file is an exact match to the signed translation. This document has not been altered.',
    fileMismatchTitle: 'File mismatch',
    fileMismatchDesc: 'The uploaded file does not match the signed translation. Even minor actions like re-saving or compressing can cause a mismatch. Please request the signed file and try again.',
    documentDetailsLabel: 'Document details',
    documentField: 'Document',
    orderField: 'Order',
    languagesField: 'Languages',
    pagesField: 'Pages',
    verifiedByField: 'Verified by',
    verifiedOnField: 'Verified on',
    verifyIntegrityLabel: 'Verify document integrity',
    computingHash: 'Computing hash…',
    dropzoneTitlePurged: 'Upload your copy of the signed PDF',
    dropzoneTitle: 'Upload the PDF provided by the translator',
    dropzoneHintPurged: 'The file is no longer on our servers. Its hash remains signed in our database and is checked against yours in your browser only.',
    dropzoneHint: 'The file is checked in your browser only. It never leaves your device.',
    fileNotAvailableBox: 'Direct file comparison is not available for this record. The translation content was cryptographically signed at the time of verification and remains unchanged on our servers.',
    disclaimerPurged: 'Zero-knowledge by design: the signed file is no longer on our servers, yet its cryptographic fingerprint is permanently stored. Upload your copy to prove it matches the file the translator signed.',
    disclaimerWithHash: 'This page alone confirms a translation is on record with this professional. It does not validate the file in your possession until you upload it for comparison.',
    disclaimerNoHash: 'This page confirms a translation is on record with this professional. Upload-based file comparison is not available for this record; integrity is guaranteed at the content level.',
    saveBtn: 'Save this verification',
    verificationResultLabel: 'Verification result',
    exactMatchTitle: 'Exact match confirmed',
    exactMatchDesc: 'The uploaded file is identical to the signed translation.',
    certificationStatementLabel: 'Certification statement',
    defaultCertStatement: 'I hereby certify that this is a true and accurate translation of the original document, to the best of my knowledge and ability.',
    verifiedDisclaimer: 'The uploaded file is an exact match to the signed translation issued by the translator. EZDocuAI verifies file integrity only. The translator is solely responsible for the content and accuracy of this translation.',
    reverifyBtn: 'Re-verify',
    fileMismatchResultTitle: 'File mismatch detected',
    fileMismatchResultDesc: 'The uploaded file does not match the signed translation.',
    mismatchDisclaimer: 'EZDocuAI does not analyze what changed. It only checks if the uploaded file is identical to the signed translation.',
    inspectedCannotConfirmTitle: 'Cannot confirm this file',
    inspectedCannotConfirmDesc: 'No file hash was stored for this verification, so we cannot confirm whether the PDF you uploaded is the one the translator signed. Please request the signed file directly from the translator.',
    yourFileHashLabel: 'Your file hash (SHA-256)',
    inspectedDisclaimer: 'Your file never left your browser — we only computed its SHA-256 locally and kept it on screen as a record. A definitive match/mismatch verdict is only possible for verifications that stored the signed PDF\'s hash at the time of signing.',
    tryAgainBtn: 'Try again',
    contactTranslatorBtn: 'Contact translator',
    mailtoSubject: 'Verification mismatch – {order}',
    mailtoBody: 'Dear {translator},\n\nI attempted to verify a translation through EZDocuAI and the uploaded file did not match the signed version on record.\n\nOrder: {order}\nDocument: {document}\n\nCould you please provide a new certified digital copy?\n\nThank you.',
    cryptographicDetails: 'Cryptographic details',
    verificationIdLabel: 'Verification ID',
    documentHashLabel: 'Document hash (SHA-256)',
    contentHashLabel: 'Content hash (SHA-256)',
    localVerificationNote: 'Local verification: the file is checked in your browser and never uploaded to any server.',
    verificationIssuedAt: 'Verification issued {date} at {time} UTC',
    poweredBy: 'Powered by',
    footerDisclaimer: 'EZDocuAI is exclusively a technology provider. We do not evaluate document content, translation accuracy, or legal compliance. This page only confirms the technical integrity of the digital file.',
    zeroKnowledgeEncrypted: 'Zero Knowledge encrypted',
    notFoundTitle: 'Verification Not Found',
    notFoundDesc: 'The translation verification certificate you are looking for does not exist or the ID is invalid.',
    goToHome: 'Go to EZDocuAI',
  },
};
